/**
 * BuildMyEvent — Prashasta Guided Event Journey
 * Premium, one-question-at-a-time experience.
 * Capability: conditional branching, live event plan, WhatsApp handoff, resume.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Check, ArrowRight, X,
  MessageCircle, MapPin, Sparkles, UtensilsCrossed, Camera,
  Mic2, Music, Users, Car, Mail, Calendar, Phone, User,
  Heart, Gem, Gift, Briefcase, MonitorPlay, Zap, Award,
  Star, ChevronDown, ChevronUp, Edit3, Calculator, AlertCircle,
  RefreshCw, Info,
} from 'lucide-react';
import {
  PJ, EVENT_TYPES, WEDDING_FUNCTIONS, CONFERENCE_TYPES,
  PLANNING_SCOPES, SERVICES, END_TO_END_DEFAULTS, CITIES,
  GUEST_RANGES, VENUE_STATUSES, STYLE_OPTIONS, BUDGET_RANGES,
  createEmptyJourney, getServiceLabel, getCityDisplay,
  formatEventDateShort, formatEventDate,
} from '../journey/journeyConfig';
import {
  loadJourneyFromStorage, saveJourneyToStorage, clearJourneyFromStorage,
  getStepSequence, getNextStep, getPrevStep, computeProgress, computeCompletionScore,
  buildWhatsAppMessage, initJourneySource, addJourneyPathEntry,
} from '../journey/journeyEngine';
import { AskPrashasta, AskPrashastaSheet } from '../journey/AskPrashasta';
import { HELP_TOPICS } from '../journey/journeyHelpConfig';
import { publicLeadApi, publicEstimateApi, publicLuxApi } from '../services/api';
import { trackPageView, trackEvent, trackGeo, trackPreLead, trackLeadCreated, getAnalyticsIds } from '../services/analyticsTracker';

// ── CRM sync storage key (separate from the journey key) ─────────────────────
const CRM_SYNC_KEY = 'prashasta_crm_sync';

// ── Service icon map ─────────────────────────────────────────────────────────
const SERVICE_ICONS = {
  MapPin: MapPin, Sparkles: Sparkles, UtensilsCrossed: UtensilsCrossed,
  Camera: Camera, Mic2: Mic2, Music: Music, Users: Users, Car: Car, Mail: Mail,
};

// ── Event type icon map ───────────────────────────────────────────────────────
const EVENT_ICONS = {
  WEDDING: Heart, ENGAGEMENT: Gem, BIRTHDAY: Gift, CORPORATE: Briefcase,
  CONFERENCE: MonitorPlay, PRODUCT_LAUNCH: Zap, AWARDS: Award, OTHER: Sparkles,
};

// ── Journey animations (injected once) ───────────────────────────────────────
const JOURNEY_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes pjFadeSlideIn { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes pjSpin        { from { transform:rotate(0deg); }               to { transform:rotate(360deg); } }
  .pj-step-enter           { animation: pjFadeSlideIn 0.38s cubic-bezier(0.22,1,0.36,1) both; }
  input[type="date"]::-webkit-calendar-picker-indicator { opacity:0.4; cursor:pointer; }
  .pj-choice-card:hover    { transform:translateY(-2px); }
  .pj-choice-card:focus    { outline:2px solid #C9A15F; outline-offset:3px; }
  .pj-btn-focus:focus      { outline:2px solid #C9A15F; outline-offset:3px; }
  .pj-sidebar-tab          { transition: all 0.18s; }
  .pj-sidebar-tab:hover    { opacity:0.85; }
  /* ── Event type visual cards ── */
  .pj-event-card { transition: transform 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.22s cubic-bezier(0.22,1,0.36,1), border-color 0.22s; }
  .pj-event-card:not([data-selected="true"]):hover { transform: translateY(-4px) scale(1.015) !important; box-shadow: 0 10px 28px rgba(32,27,25,0.18) !important; }
  .pj-event-card:focus-visible { outline: 2.5px solid #C9A15F; outline-offset: 3px; }
  @media (max-width: 640px) {
    .pj-sidebar { display:none!important; }
    .pj-mobile-plan { display:flex!important; }
    .pj-main-grid { grid-template-columns: 1fr!important; }
    .pj-ask-float { display:flex!important; }
  }
`;

// ── Choice card ───────────────────────────────────────────────────────────────
function ChoiceCard({ label, desc, Icon, selected, onClick, wide, disabled }) {
  return (
    <button
      className="pj-choice-card pj-btn-focus"
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: "'DM Sans', sans-serif",
        background: selected ? PJ.blush : PJ.white,
        border: selected ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
        borderRadius: '20px',
        padding: wide ? '20px 24px' : '18px 20px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        textAlign: 'left',
        transition: 'all 0.18s ease',
        boxShadow: selected ? `0 4px 20px -4px ${PJ.gold}30` : 'none',
        width: '100%',
        position: 'relative',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {Icon && (
        <div
          style={{
            width: '40px', height: '40px', borderRadius: '12px', flexShrink: 0,
            background: selected ? `${PJ.gold}20` : `${PJ.sand}60`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.18s',
          }}
        >
          <Icon size={18} style={{ color: selected ? PJ.gold : PJ.slate }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', color: PJ.obsidian, lineHeight: 1.2 }}>
          {label}
        </p>
        {desc && (
          <p style={{ margin: '3px 0 0', fontSize: '0.78rem', color: PJ.slate, lineHeight: 1.4 }}>
            {desc}
          </p>
        )}
      </div>
      {selected && (
        <div
          style={{
            width: '22px', height: '22px', borderRadius: '50%',
            background: PJ.gold, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Check size={12} color="white" />
        </div>
      )}
    </button>
  );
}

// ── Small function chip (for wedding functions multi-select) ──────────────────
function FunctionChip({ label, selected, onClick }) {
  return (
    <button
      className="pj-btn-focus"
      onClick={onClick}
      style={{
        fontFamily: "'DM Sans', sans-serif",
        background: selected ? PJ.blush : PJ.white,
        border: selected ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
        borderRadius: '100px',
        padding: '10px 20px',
        cursor: 'pointer',
        fontSize: '0.88rem',
        fontWeight: selected ? 600 : 400,
        color: selected ? PJ.obsidian : PJ.slate,
        transition: 'all 0.15s ease',
        display: 'flex', alignItems: 'center', gap: '6px',
      }}
    >
      {selected && <Check size={12} style={{ color: PJ.gold }} />}
      {label}
    </button>
  );
}

// ── Style chip ────────────────────────────────────────────────────────────────
function StyleChip({ label, selected, onClick, disabled }) {
  return (
    <button
      className="pj-btn-focus"
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: "'Cormorant Garamond', serif",
        background: selected ? PJ.blush : PJ.white,
        border: selected ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
        borderRadius: '14px',
        padding: '14px 22px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '1.05rem',
        fontWeight: selected ? 500 : 300,
        letterSpacing: '0.03em',
        color: selected ? PJ.obsidian : PJ.slate,
        transition: 'all 0.15s ease',
        opacity: disabled ? 0.4 : 1,
        display: 'flex', alignItems: 'center', gap: '8px',
      }}
    >
      {selected && <Check size={13} style={{ color: PJ.gold }} />}
      {label}
    </button>
  );
}

// ── Question screen shell ─────────────────────────────────────────────────────
function QuestionScreen({ eyebrow, headline, helper, children, stepKey }) {
  return (
    <div key={stepKey} className="pj-step-enter" style={{ width: '100%' }}>
      {eyebrow && (
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: PJ.gold,
          margin: '0 0 16px',
        }}>
          {eyebrow}
        </p>
      )}
      <h2 style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontWeight: 400,
        fontSize: 'clamp(1.9rem, 4vw, 2.8rem)',
        lineHeight: 1.12,
        color: PJ.obsidian,
        margin: '0 0 12px',
        letterSpacing: '-0.01em',
      }}>
        {headline}
      </h2>
      {helper && (
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.9rem', color: PJ.slate,
          lineHeight: 1.6, margin: '0 0 28px',
          maxWidth: '440px',
        }}>
          {helper}
        </p>
      )}
      {!helper && <div style={{ marginBottom: '28px' }} />}
      {children}
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function JourneyProgress({ progress }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '4px',
        padding: '0 max(24px, 5vw)',
        overflowX: 'auto', scrollbarWidth: 'none',
      }}
    >
      {progress.stages.map((stage, i) => (
        <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 10px', borderRadius: '100px',
              background: stage.status === 'ACTIVE' ? `${PJ.ruby}14` : 'transparent',
              transition: 'all 0.2s',
            }}
          >
            {stage.status === 'DONE' && (
              <Check size={12} style={{ color: PJ.forest }} />
            )}
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.72rem', fontWeight: stage.status === 'ACTIVE' ? 700 : 500,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                color: stage.status === 'ACTIVE' ? PJ.ruby : stage.status === 'DONE' ? PJ.forest : PJ.sand,
                transition: 'color 0.2s',
              }}
            >
              {stage.label}
            </span>
          </div>
          {i < progress.stages.length - 1 && (
            <div
              style={{
                width: '20px', height: '1px',
                background: stage.status === 'DONE' ? PJ.forest : PJ.sand,
                transition: 'background 0.3s',
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ── Live Event Plan (sidebar + mobile) ────────────────────────────────────────
function LiveEventPlan({ state, compact, onEdit }) {
  const eventType = EVENT_TYPES.find(e => e.value === state?.event_type?.value)?.label;
  const city = getCityDisplay(state);
  const guests = state?.guest_count_range?.display || GUEST_RANGES.find(g => g.value === state?.guest_count_range?.value)?.display;
  const scope = state?.planning_scope?.value;
  const services = state?.service_interests?.value || [];
  // Map raw style values to human labels
  const styleValues = state?.style_preferences?.value || [];
  const styles = styleValues.map(v => STYLE_OPTIONS.find(s => s.value === v)?.label || v);
  const budget = state?.budget_range?.display || BUDGET_RANGES.find(b => b.value === state?.budget_range?.value)?.display;
  const date = state?.event_date?.display || (state?.event_date?.value ? formatEventDateShort(state.event_date.value) : '');
  // Map raw wedding function values to human labels
  const functionValues = state?.wedding_functions?.value || [];
  const functions = functionValues.map(v => WEDDING_FUNCTIONS.find(f => f.value === v)?.label || v);

  const hasAny = !!(eventType || city || guests || scope || date);

  if (!hasAny && !compact) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 24px' }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem', color: PJ.slate, margin: 0, fontStyle: 'italic' }}>
          Your event is taking shape...
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: PJ.slate }}>
          Your Event
        </p>
        {onEdit && (
          <button
            onClick={onEdit}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: PJ.slate, padding: '4px' }}
          >
            <Edit3 size={13} />
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {eventType && (
          <div>
            <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: 400, color: PJ.obsidian, lineHeight: 1.1 }}>
              {eventType}
            </p>
            {functions.length > 0 && (
              <p style={{ margin: '4px 0 0', fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: PJ.slate }}>
                {functions.slice(0, 3).join(' · ')}
                {functions.length > 3 && ` +${functions.length - 3} more`}
              </p>
            )}
          </div>
        )}
        {date && (
          <div>
            <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: PJ.slate }}>{date}</p>
          </div>
        )}
        {city && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MapPin size={13} style={{ color: PJ.gold }} />
            <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: PJ.slate }}>{city}</p>
          </div>
        )}
        {guests && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Users size={13} style={{ color: PJ.gold }} />
            <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: PJ.slate }}>{guests} guests</p>
          </div>
        )}

        {scope && (
          <>
            <div style={{ height: '1px', background: PJ.sand }} />
            <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: PJ.obsidian }}>
              {scope === 'END_TO_END' ? 'End-to-End Planning' : 'Selected Services'}
            </p>
            {services.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '-4px' }}>
                {services.slice(0, 6).map(s => (
                  <span key={s} style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '0.72rem', padding: '3px 8px',
                    borderRadius: '100px', background: `${PJ.sand}80`,
                    color: PJ.slate,
                  }}>
                    {getServiceLabel(s)}
                  </span>
                ))}
                {services.length > 6 && (
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: PJ.slate, padding: '3px 4px' }}>
                    +{services.length - 6}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        {styles.length > 0 && (
          <>
            <div style={{ height: '1px', background: PJ.sand }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {styles.map((s, i) => (
                <span key={i} style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: '0.88rem', fontStyle: 'italic',
                  padding: '2px 10px', borderRadius: '100px',
                  background: `${PJ.blush}80`, color: PJ.obsidian,
                }}>
                  {s}
                </span>
              ))}
            </div>
          </>
        )}

        {budget && (
          <>
            <div style={{ height: '1px', background: PJ.sand }} />
            <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: PJ.slate }}>
              Investment: <strong style={{ color: PJ.obsidian }}>{budget}</strong>
            </p>
          </>
        )}

        {hasAny && (
          <div style={{ marginTop: '4px' }}>
            <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '0.95rem', fontStyle: 'italic', color: PJ.gold }}>
              Your event is taking shape ✦
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── WhatsApp Handoff Modal ────────────────────────────────────────────────────
function WhatsAppModal({ state, onClose }) {
  const msg = buildWhatsAppMessage(state);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(msg).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const waUrl = `https://wa.me/91900099277?text=${encodeURIComponent(msg)}`;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(32,27,25,0.5)', backdropFilter: 'blur(4px)' }}
      />
      <div
        data-testid="whatsapp-modal"
        style={{
          position: 'relative', background: PJ.white, borderRadius: '24px',
          padding: '32px', maxWidth: '440px', width: '100%',
          boxShadow: '0 24px 60px -8px rgba(32,27,25,0.25)',
          animation: 'pjFadeSlideIn 0.3s ease both',
        }}
      >
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: PJ.slate, padding: '6px' }}
        >
          <X size={18} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#25D36620', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MessageCircle size={20} style={{ color: '#25D366' }} />
          </div>
          <div>
            <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 700, color: PJ.obsidian }}>
              WhatsApp Planner
            </p>
            <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: PJ.slate }}>
              Your event details will travel with you
            </p>
          </div>
        </div>

        <div
          style={{
            background: PJ.ivory, borderRadius: '14px', padding: '16px',
            marginBottom: '16px', border: `1px solid ${PJ.sand}`,
          }}
        >
          <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.obsidian, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
            {msg}
          </p>
        </div>

        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: PJ.slate, lineHeight: 1.5, margin: '0 0 20px' }}>
          Your event details will travel with you, so you don't need to repeat everything to your planner.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-open-btn"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              background: '#25D366', color: 'white', borderRadius: '100px',
              padding: '14px', textDecoration: 'none',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            <MessageCircle size={17} />
            Open WhatsApp
          </a>
          <button
            onClick={handleCopy}
            style={{
              background: 'none', border: `1.5px solid ${PJ.sand}`,
              borderRadius: '100px', padding: '12px',
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem',
              color: PJ.slate, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = PJ.obsidian; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = PJ.sand; }}
          >
            {copied ? <><Check size={14} style={{ color: PJ.forest }} /> Copied!</> : 'Copy message'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quick Call mode (30-second path) ─────────────────────────────────────────
function QuickCallMode({ onDone, onSwitch }) {
  const [step, setStep] = useState(0);
  const [eventType, setEventType] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [callTime, setCallTime] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <div className="pj-step-enter" style={{ textAlign: 'center', padding: '40px 24px' }}>
        <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: `${PJ.forest}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Check size={24} style={{ color: PJ.forest }} />
        </div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.8rem', fontWeight: 400, color: PJ.obsidian, margin: '0 0 12px' }}>
          Request received
        </h2>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: PJ.slate, lineHeight: 1.6, margin: '0 0 24px' }}>
          A Prashasta planner will call you at your preferred time.
        </p>
        <button
          onClick={onSwitch}
          style={{
            background: 'none', border: `1.5px solid ${PJ.sand}`, borderRadius: '100px',
            padding: '12px 24px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem',
            color: PJ.slate, cursor: 'pointer',
          }}
        >
          Build my event instead →
        </button>
      </div>
    );
  }

  return (
    <div className="pj-step-enter" style={{ maxWidth: '420px' }}>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: PJ.gold, margin: '0 0 16px' }}>
        Quick Connect
      </p>
      <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.2rem', fontWeight: 400, color: PJ.obsidian, margin: '0 0 24px' }}>
        I just want someone<br />to call me.
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
        <div>
          <label style={{ display: 'block', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: PJ.slate, marginBottom: '8px', letterSpacing: '0.04em' }}>
            What are you planning?
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {['Wedding', 'Birthday', 'Corporate', 'Conference', 'Other'].map(t => (
              <button
                key={t}
                onClick={() => setEventType(t)}
                style={{
                  background: eventType === t ? PJ.blush : PJ.white,
                  border: eventType === t ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                  borderRadius: '100px', padding: '8px 16px', cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem',
                  color: eventType === t ? PJ.obsidian : PJ.slate,
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {[
          { label: 'Your name', value: name, set: setName, type: 'text', icon: User, placeholder: 'e.g. Priya Sharma', testid: 'quick-name' },
          { label: 'Mobile number', value: mobile, set: setMobile, type: 'tel', icon: Phone, placeholder: '+91 XXXXX XXXXX', testid: 'quick-mobile' },
        ].map(({ label, value, set, type, icon: Icon, placeholder, testid }) => (
          <div key={label}>
            <label style={{ display: 'block', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: PJ.slate, marginBottom: '8px' }}>
              {label}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', border: `1.5px solid ${PJ.sand}`, borderRadius: '12px', padding: '12px 14px', background: PJ.white }}>
              <Icon size={16} style={{ color: PJ.gold, flexShrink: 0 }} />
              <input
                type={type} value={value} onChange={e => set(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                style={{ flex: 1, border: 'none', background: 'transparent', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: PJ.obsidian, outline: 'none' }}
              />
            </div>
          </div>
        ))}

        <div>
          <label style={{ display: 'block', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: PJ.slate, marginBottom: '8px' }}>
            Preferred callback time
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {['Morning (9–12)', 'Afternoon (12–5)', 'Evening (5–8)', 'Any time'].map(t => (
              <button
                key={t}
                onClick={() => setCallTime(t)}
                style={{
                  background: callTime === t ? PJ.blush : PJ.white,
                  border: callTime === t ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                  borderRadius: '100px', padding: '8px 14px', cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem',
                  color: callTime === t ? PJ.obsidian : PJ.slate,
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={() => { if (name && mobile) setSubmitted(true); }}
        disabled={!name || !mobile}
        data-testid="quick-call-submit"
        style={{
          background: !name || !mobile ? PJ.sand : PJ.ruby,
          color: !name || !mobile ? PJ.slate : PJ.white,
          border: 'none', borderRadius: '100px', padding: '14px 28px', width: '100%',
          fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem',
          cursor: !name || !mobile ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
        }}
      >
        Request a Planner Call
      </button>

      <button
        onClick={onSwitch}
        style={{ marginTop: '12px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.slate, width: '100%', textAlign: 'center', padding: '8px' }}
      >
        Build my event step by step →
      </button>
    </div>
  );
}

// ── GQP-03: Guest count default suggestions per range ────────────────────────
const GUEST_RANGE_DEFAULTS = {
  UNDER_100: '75', '100_250': '150', '250_500': '300',
  '500_750': '600', '750_1000': '875', '1000_PLUS': '1000', NOT_SURE: '',
};
const TENANT_ALIAS = 'prashasta';

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function BuildMyEvent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isQuickMode = searchParams.get('mode') === 'quick';

  const [quickMode, setQuickMode] = useState(isQuickMode);
  const [journeyState, setJourneyState] = useState(() => {
    const saved = loadJourneyFromStorage();
    return saved || createEmptyJourney();
  });
  const [currentStepId, setCurrentStepId] = useState(() => {
    const saved = loadJourneyFromStorage();
    return saved?.journey_step || 'EVENT_TYPE';
  });
  const [stepKey, setStepKey] = useState(0);
  const [showPlanMobile, setShowPlanMobile] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [cityInput, setCityInput] = useState('');
  const [multiSelections, setMultiSelections] = useState({});
  const [contactForm, setContactForm] = useState({
    name: journeyState?.contact?.name || '',
    mobile: journeyState?.contact?.mobile || '',
    email: journeyState?.contact?.email || '',
  });
  const [dateInput, setDateInput]           = useState(journeyState?.event_date?.value || '');
  const [datePrecision, setDatePrecision]   = useState('');   // 'EXACT' | 'MONTH' | 'DECIDING' | ''
  const [scopeConfirmCustomize, setScopeConfirmCustomize] = useState(false);
  const [endToEndAccepted, setEndToEndAccepted] = useState(false);
  const [sidebarMode, setSidebarMode] = useState('PLAN'); // 'PLAN' | 'ASK'
  const [askSheetOpen, setAskSheetOpen] = useState(false);
  const [pendingEventType, setPendingEventType] = useState(null);
  // ── P0: Public Event Request Journey submission state ───────────────────────
  const [submitState, setSubmitState] = useState({ status: 'idle', reference: null });
  const [consentGiven, setConsentGiven] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [showSubmittedDetails, setShowSubmittedDetails] = useState(false);

  // ── GQP-03: Event Quote Planner state ────────────────────────────────────────
  // Default guest count suggestions per range (helpful UX pre-fill, user can edit)
  const [estimateGuestCount, setEstimateGuestCount] = useState(() =>
    GUEST_RANGE_DEFAULTS[loadJourneyFromStorage()?.guest_count_range?.value] || ''
  );
  const [estimateBudgetPref, setEstimateBudgetPref] = useState(() =>
    loadJourneyFromStorage()?.budget_range?.value || ''
  );
  const [estimateState, setEstimateState] = useState({ status: 'idle', data: null, error: null });
  const [serviceMap, setServiceMap] = useState(null); // { BME_CODE: service_id }
  const [serviceMapLoading, setServiceMapLoading] = useState(false);
  const [estimateSnapshot, setEstimateSnapshot] = useState(null); // services + guest count at last calc
  const estimateAbortRef = useRef(null);

  // ── GQP-LUX-01: Journey mode + luxury state ──────────────────────────────────
  const [journeyMode, setJourneyMode] = useState(() => {
    const saved = loadJourneyFromStorage();
    return saved?.journey_mode || null; // null = show mode selection screen
  });
  const [luxuryStep, setLuxuryStep] = useState('LUX_DETAILS');
  const [luxuryState, setLuxuryState] = useState({
    eventType: null,
    city: '',
    date: '',
    guestCount: 300,
    functions: [],
    disabledServices: [],
  });
  const [luxuryEstimate, setLuxuryEstimate] = useState(null);
  const [luxuryLoading, setLuxuryLoading] = useState(false);
  const [luxuryError, setLuxuryError] = useState(null);
  // ── GQP-LUX-02: Preferred callback scheduling (shared luxury + custom) ────────
  const [callbackPref, setCallbackPref] = useState({
    requested: false,
    date: '',
    window: 'ANYTIME',
    timezone: 'Asia/Kolkata',
    method: 'PHONE',
    notes: '',
    anytime: false,
  });

  // ── EXP-01E: Remove Inspiration ─────────────────────────────────────────────
  // Clears inspiration metadata + any fields still tagged INSPIRATION_SUGGESTED.
  // Fields the customer has already confirmed (CUSTOMER_CONFIRMED) are untouched.
  const handleRemoveInspiration = useCallback(() => {
    const current = journeyState || {};
    const suggestedKeys = current.inspiration?.suggested_field_keys || [];
    const next = { ...current };
    delete next.inspiration;
    suggestedKeys.forEach(key => {
      if (next[key]?.source === 'INSPIRATION_SUGGESTED') delete next[key];
    });
    setJourneyState(next);
    saveJourneyToStorage(next);
  }, [journeyState]);

  // ── Capture source attribution + generate stable journey_session_id ────────
  useEffect(() => {
    const saved = loadJourneyFromStorage();
    // Generate a stable session ID once per journey — never overwrite an existing one
    const sessionId = saved?.journey_session_id || (
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `bme-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    );

    if (!saved?.source_captured) {
      const entryPoint = new URLSearchParams(window.location.search).get('entry') || 'BUILD_MY_EVENT';
      const sourceData = initJourneySource(entryPoint, saved);
      const path = addJourneyPathEntry(saved || {}, 'BUILD_MY_EVENT');
      const next = { ...(saved || journeyState), ...sourceData, journey_path: path, journey_session_id: sessionId };
      setJourneyState(next);
      saveJourneyToStorage(next);
    } else if (saved) {
      // Backfill session ID on older saved journeys
      const needsSessionId = !saved.journey_session_id;
      const path = addJourneyPathEntry(saved, 'BUILD_MY_EVENT');
      const pathChanged = path.length !== (saved.journey_path || []).length;
      if (needsSessionId || pathChanged) {
        const next = { ...saved, journey_path: path, journey_session_id: sessionId };
        setJourneyState(next);
        saveJourneyToStorage(next);
      }
    }
    // Analytics: Build My Event page view
    trackPageView('/build-my-event');
    trackEvent('BUILD_EVENT_STARTED', { page_path: '/build-my-event' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── P0: Restore submitted state from localStorage on RESULT step ─────────────
  // Prevents automatic re-submission on page refresh. If the guest has already
  // submitted this session, restore the confirmation state from localStorage.
  useEffect(() => {
    if (currentStepId !== 'RESULT') return;
    try {
      const stored = JSON.parse(localStorage.getItem(CRM_SYNC_KEY) || 'null');
      if (
        stored?.crm_sync_status === 'SYNCED' &&
        stored?.journey_session_id === journeyState?.journey_session_id &&
        stored?.public_reference
      ) {
        setSubmitState({ status: 'submitted', reference: stored.public_reference });
      }
    } catch { /* ignore corrupt data */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId]);

  // ── P0: handleSubmitRequest — explicit user action triggers CRM intake ────────
  // Idempotent: journey_session_id is the server-side deduplication key.
  // Returns only an opaque public_reference — no internal CRM IDs ever stored.
  const handleSubmitRequest = useCallback(async () => {
    if (!consentGiven) return;
    if (submitState.status === 'submitting' || submitState.status === 'submitted') return;

    setSubmitState({ status: 'submitting', reference: null });

    const sessionId = journeyState?.journey_session_id;
    const name = journeyMode === 'LUXURY'
      ? (contactForm?.name?.trim() || '')
      : (journeyState?.contact?.name?.trim() || '');
    const mobile = journeyMode === 'LUXURY'
      ? (contactForm?.mobile?.trim() || '')
      : (journeyState?.contact?.mobile?.trim() || '');
    const contactEmail = journeyMode === 'LUXURY'
      ? (contactForm?.email?.trim() || '')
      : (journeyState?.contact?.email || '');
    const consentTimestamp = new Date().toISOString();

    const payload = {
      journey_session_id: sessionId,
      source_channel: journeyState.source_channel || 'WEBSITE',
      source_touchpoint: journeyState.source_touchpoint || 'BUILD_MY_EVENT',
      source_entry_point: journeyState.source_entry_point,
      journey_path: journeyState.journey_path || [],
      contact_name: name,
      contact_mobile: mobile,
      contact_email: contactEmail,
      event_type:        journeyMode === 'LUXURY' ? luxuryState.eventType : journeyState.event_type?.value,
      event_date:        journeyMode === 'LUXURY' ? luxuryState.date       : journeyState.event_date?.value,
      event_city:        journeyMode === 'LUXURY' ? luxuryState.city       : journeyState.city?.value,
      venue_status:      journeyState.venue_status?.value,
      guest_count_range: journeyMode === 'LUXURY' ? null                   : journeyState.guest_count_range?.value,
      budget_range:      journeyState.budget_range?.value,
      planning_scope:    journeyState.planning_scope?.value,
      style_preferences: journeyState.style_preferences?.value || [],
      service_interests: journeyMode === 'LUXURY'
        ? (luxuryEstimate?.service_meta || []).filter(s => s.is_included).map(s => s.service_code)
        : (journeyState.service_interests?.value || []),
      event_functions:   journeyMode === 'LUXURY' ? luxuryState.functions : (journeyState.wedding_functions?.value || []),
      intent_completion_pct: computeCompletionScore(journeyState),
      inspiration_slug:  journeyState.inspiration?.selected_match_slug || null,
      inspiration_title: journeyState.inspiration?.inspiration_title   || null,
      // P0: Explicit consent fields
      consent_given: consentGiven,
      consent_wording: "I agree that Prashasta Events may contact me regarding my event request by phone, WhatsApp or email, using the contact details I provided.",
      consent_version: "CONTACT_CONSENT_V1_0",
      consent_channel_scope: ["PHONE", "WHATSAPP", "EMAIL"],
      consent_timestamp: consentTimestamp,
      marketing_consent: marketingConsent,
      consent: consentGiven, // backward compat
      // GQP-03: Quote planner reference (for GQP-04 CRM linkage)
      estimate_ref: journeyMode === 'LUXURY'
        ? (luxuryEstimate?.estimate_ref || null)
        : (journeyState.estimate_ref || null),
      guest_count_exact: journeyMode === 'LUXURY'
        ? (luxuryState.guestCount || null)
        : (journeyState.guest_count_exact || null),
      // GQP-LUX-01: Luxury concierge fields
      planning_mode: journeyMode === 'LUXURY' ? 'LUXURY_CONCIERGE' : 'CUSTOM_EVENT',
      luxury_template_id: luxuryEstimate?.template_id || null,
      luxury_template_version: luxuryEstimate?.template_version || null,
      luxury_selected_service_ids: (luxuryEstimate?.service_meta || [])
        .filter(s => s.is_included).map(s => s.service_id),
      // GQP-LUX-02: Preferred callback scheduling
      callback_requested: callbackPref.requested,
      callback_date: callbackPref.requested && !callbackPref.anytime ? callbackPref.date : null,
      callback_window: callbackPref.requested ? callbackPref.window : null,
      callback_timezone: callbackPref.timezone || 'Asia/Kolkata',
      callback_method: callbackPref.method || 'PHONE',
      callback_notes: callbackPref.notes || null,
      callback_anytime: callbackPref.anytime,
    };

    const doAttempt = async () => {
      const analyticsIds = getAnalyticsIds();
      const r = await publicLeadApi.createProvisionalLead({
        ...payload,
        visitor_id: analyticsIds.visitor_id,
        analytics_session_id: analyticsIds.analytics_session_id,
        event_postal_code: journeyState.postal_code?.value || null,
      });
      return r.data;
    };

    try {
      const result = await doAttempt();
      const pubRef = result.public_reference;
      const syncState = {
        crm_sync_status: 'SYNCED',
        public_reference: pubRef,
        journey_session_id: sessionId,
        last_sync_attempt: new Date().toISOString(),
      };
      try { localStorage.setItem(CRM_SYNC_KEY, JSON.stringify(syncState)); } catch { /* storage full */ }
      // Analytics
      trackEvent('BUILD_EVENT_COMPLETED', { page_path: '/build-my-event', event_type_category: journeyState.event_type?.value });
      trackPreLead({
        prelead_reference: sessionId,
        event_type: journeyState.event_type?.value,
        event_city: journeyState.city?.value,
        event_postal_code: journeyState.postal_code?.value,
        preferred_date: journeyState.event_date?.value,
        intent_source: 'BUILD_MY_EVENT',
      });
      trackLeadCreated({ lead_reference_id: sessionId, prelead_reference: sessionId, conversion_source: 'BUILD_MY_EVENT' });
      setSubmitState({ status: 'submitted', reference: pubRef });
    } catch {
      // One safe retry — idempotent API (journey_session_id key) makes this safe
      try {
        const result = await doAttempt();
        const pubRef = result.public_reference;
        const syncState = { crm_sync_status: 'SYNCED', public_reference: pubRef, journey_session_id: sessionId, last_sync_attempt: new Date().toISOString() };
        try { localStorage.setItem(CRM_SYNC_KEY, JSON.stringify(syncState)); } catch { /* ignore */ }
        setSubmitState({ status: 'submitted', reference: pubRef });
      } catch {
        const failState = { crm_sync_status: 'FAILED', journey_session_id: sessionId, last_sync_attempt: new Date().toISOString() };
        try { localStorage.setItem(CRM_SYNC_KEY, JSON.stringify(failState)); } catch { /* ignore */ }
        setSubmitState({ status: 'error', reference: null });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentGiven, marketingConsent, journeyState, submitState.status]);

  // ── P0: handleStartNewPlan — clear draft state, preserve submitted CRM record ─
  const handleStartNewPlan = useCallback(() => {
    clearJourneyFromStorage();
    // Preserve CRM sync record (don't delete submitted lead reference)
    const newJourney = createEmptyJourney();
    setJourneyState(newJourney);
    setCurrentStepId('EVENT_TYPE');
    setSubmitState({ status: 'idle', reference: null });
    setConsentGiven(false);
    setMarketingConsent(false);
    setShowSubmittedDetails(false);
    // GQP-03: reset estimate state
    setEstimateGuestCount('');
    setEstimateBudgetPref('');
    setEstimateState({ status: 'idle', data: null, error: null });
    setEstimateSnapshot(null);
    // GQP-LUX: reset luxury + callback state
    setJourneyMode(null);
    setLuxuryStep('LUX_DETAILS');
    setLuxuryState({ eventType: null, city: '', date: '', guestCount: 300, functions: [], disabledServices: [] });
    setLuxuryEstimate(null);
    setLuxuryLoading(false);
    setLuxuryError(null);
    setCallbackPref({ requested: false, date: '', window: 'ANYTIME', timezone: 'Asia/Kolkata', method: 'PHONE', notes: '', anytime: false });
    setStepKey(k => k + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync multi-selections from state when step changes
  useEffect(() => {
    if (currentStepId === 'WEDDING_FUNCTIONS') {
      setMultiSelections(prev => ({ ...prev, WEDDING_FUNCTIONS: journeyState?.wedding_functions?.value || [] }));
    }
    if (currentStepId === 'SERVICE_SELECTION') {
      setMultiSelections(prev => ({ ...prev, SERVICE_SELECTION: journeyState?.service_interests?.value || [] }));
    }
    if (currentStepId === 'STYLE_PREF') {
      setMultiSelections(prev => ({ ...prev, STYLE_PREF: journeyState?.style_preferences?.value || [] }));
    }
    // If this step has no help topics, always show Plan
    if (!HELP_TOPICS[currentStepId]) setSidebarMode('PLAN');
  }, [currentStepId]);

  const progress = computeProgress(journeyState, currentStepId);

  // ── GQP-03: Load service map and pre-fill guest count on ESTIMATE step ────────
  useEffect(() => {
    if (currentStepId !== 'ESTIMATE' && currentStepId !== 'BUDGET') return;

    // Pre-fill guest count from range if not already set
    if (!estimateGuestCount) {
      const rangeVal = journeyState?.guest_count_range?.value;
      const defaultCount = GUEST_RANGE_DEFAULTS[rangeVal] || '';
      if (defaultCount) setEstimateGuestCount(defaultCount);
    }

    // Restore estimate from journey state if available
    if (journeyState?.estimate_data && estimateState.status === 'idle') {
      setEstimateState({ status: 'success', data: journeyState.estimate_data, error: null });
      setEstimateSnapshot({
        services: journeyState?.service_interests?.value || [],
        guestCount: journeyState?.guest_count_exact?.toString() || '',
      });
    }

    // Load service map if not already loaded
    if (serviceMap || serviceMapLoading) return;
    setServiceMapLoading(true);
    publicEstimateApi.getServiceMap(TENANT_ALIAS)
      .then(r => setServiceMap(r.data?.service_map || {}))
      .catch(() => setServiceMap({}))
      .finally(() => setServiceMapLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId]);

  // Helper: check if estimate is stale (inputs changed since last calculation)
  const isEstimateStale = () => {
    if (!estimateSnapshot) return false;
    const services = journeyState?.service_interests?.value || [];
    return (
      estimateSnapshot.guestCount !== estimateGuestCount ||
      JSON.stringify(estimateSnapshot.services.sort()) !== JSON.stringify([...services].sort())
    );
  };

  // Format minor units (paise) as ₹ display string
  const fmtINR = (minor) => {
    if (minor == null) return '—';
    return `₹${(minor / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Trigger estimate calculation
  const handleCalculateEstimate = async () => {
    const guestCount = parseInt(estimateGuestCount, 10);
    if (!guestCount || guestCount < 1) return;
    if (!journeyState?.event_date?.value) return;

    // Cancel any in-flight request
    if (estimateAbortRef.current) estimateAbortRef.current = true;
    const abortToken = {};
    estimateAbortRef.current = abortToken;

    setEstimateState({ status: 'loading', data: null, error: null });

    const bmeServices = journeyState?.service_interests?.value || [];
    const resolvedServices = [];
    const unmappedServices = [];

    if (serviceMap) {
      bmeServices.forEach(code => {
        const sid = serviceMap[code];
        if (sid) {
          // Use guest_count as quantity for PER_PERSON services; default 1 for others
          const qty = 1; // GQP-02 engine handles PER_PERSON with guest_count in request
          resolvedServices.push({ service_id: sid, quantity: qty });
        } else {
          unmappedServices.push(code);
        }
      });
    }

    if (resolvedServices.length === 0) {
      setEstimateState({
        status: 'unavailable',
        data: null,
        error: 'Pricing is not yet configured for your selected services. Your planner will provide a detailed quote.',
      });
      return;
    }

    try {
      const r = await publicEstimateApi.calculate({
        tenant_alias: TENANT_ALIAS,
        event_date: journeyState.event_date.value,
        event_type_code: journeyState.event_type?.value || 'OTHER',
        guest_count: guestCount,
        location_city: journeyState.city?.value || '',
        services: resolvedServices,
        currency: 'INR',
        caller_idempotency_key: journeyState.journey_session_id || `bme-${Date.now()}`,
      });

      if (abortToken !== estimateAbortRef.current) return; // stale response

      const data = r.data;
      setEstimateState({ status: 'success', data, error: null });
      setEstimateSnapshot({ services: bmeServices, guestCount: estimateGuestCount });

      // Persist estimate ref + data in journey state (for CRM submission + resume)
      const next = {
        ...journeyState,
        estimate_ref: data.estimate_ref,
        estimate_data: data,
        guest_count_exact: guestCount,
        budget_range: estimateBudgetPref
          ? { value: estimateBudgetPref, source: 'CUSTOMER_CONFIRMED', confirmed: true }
          : journeyState.budget_range,
      };
      setJourneyState(next);
      saveJourneyToStorage(next);
    } catch (err) {
      if (abortToken !== estimateAbortRef.current) return;
      setEstimateState({
        status: 'error',
        data: null,
        error: 'We could not generate an estimate right now. Your planner will confirm pricing details.',
      });
    }
  };

  // ── State update helper ──────────────────────────────────────────────────────
  const updateState = useCallback((updates) => {
    const next = { ...journeyState, ...updates, journey_step: currentStepId };
    setJourneyState(next);
    saveJourneyToStorage(next);
    return next;
  }, [journeyState, currentStepId]);

  // ── Navigation ───────────────────────────────────────────────────────────────
  const navigateToStep = useCallback((stepId, latestState) => {
    const stateToUse = latestState || journeyState;
    const updated = { ...stateToUse, journey_step: stepId };
    setJourneyState(updated);
    saveJourneyToStorage(updated);
    setCurrentStepId(stepId);
    setStepKey(k => k + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Analytics: step transition tracking
    if (stepId === 'CONTACT_INFO' || stepId === 'RESULT') {
      trackEvent('INQUIRY_STARTED', {
        page_path: '/build-my-event',
        event_type_category: stateToUse?.event_type?.label || stateToUse?.event_type?.value,
      });
    }
  }, [journeyState]); // eslint-disable-line

  const advance = useCallback((latestState) => {
    const s = latestState || journeyState;
    const next = getNextStep(s, currentStepId);
    if (next) {
      setSidebarMode('PLAN'); // reveal the updated plan after each decision
      navigateToStep(next, s);
    }
  }, [journeyState, currentStepId, navigateToStep]);

  const goBack = useCallback(() => {
    const prev = getPrevStep(journeyState, currentStepId);
    if (prev) navigateToStep(prev);
    else navigate('/event-journey');
  }, [journeyState, currentStepId, navigateToStep, navigate]);

  // ── Single-choice selection + auto-advance ────────────────────────────────
  const selectAndAdvance = useCallback((field, value, extraUpdates = {}) => {
    // extraUpdates takes precedence over the default field value (preserves label/display)
    const next = { ...journeyState, [field]: { value, source: 'CUSTOMER_CONFIRMED', confirmed: true }, ...extraUpdates, journey_step: currentStepId };
    setJourneyState(next);
    saveJourneyToStorage(next);
    // Brief delay so selection state is visible
    setTimeout(() => {
      const nextStep = getNextStep(next, currentStepId);
      if (nextStep) {
        setSidebarMode('PLAN'); // show updated plan after selection
        setCurrentStepId(nextStep);
        setStepKey(k => k + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 320);
  }, [journeyState, currentStepId]);

  // ── Multi-select toggle ──────────────────────────────────────────────────────
  const toggleMulti = (key, value, max) => {
    const current = multiSelections[key] || [];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : (max && current.length >= max ? current : [...current, value]);
    setMultiSelections(prev => ({ ...prev, [key]: next }));
  };

  const confirmMultiAndAdvance = (field, key, stateKey) => {
    const values = multiSelections[key] || [];
    const next = updateState({ [stateKey]: { value: values, source: 'CUSTOMER_CONFIRMED', confirmed: true } });
    advance(next);
  };

  // ════════════════════════════════════════════════════════════════════════════
  // STEP RENDERERS
  // ════════════════════════════════════════════════════════════════════════════

  const handleEventTypeSelect = (et) => {
    if (pendingEventType) return;
    setPendingEventType(et.value);
    // Analytics: Event Type Viewed / Selected
    trackEvent('EVENT_TYPE_VIEWED', {
      page_path: '/build-my-event',
      event_type_category: et.label || et.value,
      metadata: { event_type_label: et.label },
    });
    setTimeout(() => {
      selectAndAdvance('event_type', et.value, {
        event_type: { value: et.value, label: et.label, source: 'CUSTOMER_CONFIRMED', confirmed: true },
      });
      setPendingEventType(null);
    }, 280);
  };

  const renderEventType = () => (
    <QuestionScreen
      eyebrow="The Event"
      headline="What are we celebrating?"
      helper="Choose the occasion and we'll shape the journey around it."
      stepKey={stepKey}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
        gap: '12px',
      }}>
        {EVENT_TYPES.map(et => {
          const Icon = EVENT_ICONS[et.value];
          const isSelected = journeyState?.event_type?.value === et.value || pendingEventType === et.value;
          return (
            <button
              key={et.value}
              data-testid={`event-type-${et.value.toLowerCase()}`}
              data-selected={isSelected ? 'true' : 'false'}
              className="pj-event-card"
              onClick={() => handleEventTypeSelect(et)}
              disabled={!!pendingEventType && !isSelected}
              aria-label={et.label}
              style={{
                position: 'relative',
                borderRadius: '16px',
                overflow: 'hidden',
                aspectRatio: '4/3',
                cursor: pendingEventType ? (isSelected ? 'default' : 'not-allowed') : 'pointer',
                border: `2.5px solid ${isSelected ? PJ.ruby : 'transparent'}`,
                transform: isSelected ? 'translateY(-4px) scale(1.02)' : 'none',
                boxShadow: isSelected
                  ? `0 12px 32px rgba(217,30,72,0.25), 0 4px 12px rgba(32,27,25,0.1)`
                  : '0 2px 10px rgba(32,27,25,0.09)',
                background: et.gradient || PJ.sand,
                padding: 0,
                textAlign: 'left',
                outline: 'none',
              }}
            >
              {/* Photo — hides on load error, gradient shows through */}
              <img
                src={et.image}
                alt={et.label}
                loading="lazy"
                onError={e => { e.currentTarget.style.display = 'none'; }}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  filter: isSelected ? 'brightness(0.82)' : 'none',
                  transition: 'filter 0.22s',
                }}
              />

              {/* Ruby tint on selected */}
              {isSelected && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: `${PJ.ruby}18`,
                }} />
              )}

              {/* Dark gradient for text readability — always rendered */}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to bottom, rgba(32,27,25,0) 25%, rgba(32,27,25,0.70) 100%)',
                pointerEvents: 'none',
              }} />

              {/* Checkmark badge */}
              {isSelected && (
                <div style={{
                  position: 'absolute', top: '10px', right: '10px',
                  width: '24px', height: '24px', borderRadius: '50%',
                  background: PJ.ruby,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 10px rgba(217,30,72,0.45)',
                }}>
                  <Check size={12} style={{ color: PJ.white }} />
                </div>
              )}

              {/* Label area */}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                padding: '10px 13px 12px',
                pointerEvents: 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
                  {Icon && (
                    <Icon size={11} style={{ color: PJ.gold, flexShrink: 0 }} />
                  )}
                  <p style={{
                    margin: 0,
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: '1.2rem',
                    fontWeight: 500,
                    color: PJ.white,
                    lineHeight: 1.1,
                    letterSpacing: '0.02em',
                  }}>
                    {et.label}
                  </p>
                </div>
                <p style={{
                  margin: 0,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '0.67rem',
                  color: 'rgba(255,255,255,0.72)',
                  lineHeight: 1.3,
                }}>
                  {et.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </QuestionScreen>
  );

  const renderWeddingFunctions = () => {
    const selected = multiSelections['WEDDING_FUNCTIONS'] || journeyState?.wedding_functions?.value || [];
    return (
      <QuestionScreen
        eyebrow="The Event"
        headline="Which celebrations are part of your wedding?"
        helper="Select all that you'd like to plan together."
        stepKey={stepKey}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '28px' }}>
          {WEDDING_FUNCTIONS.map(fn => (
            <FunctionChip
              key={fn.value}
              label={fn.label}
              selected={selected.includes(fn.value)}
              onClick={() => toggleMulti('WEDDING_FUNCTIONS', fn.value)}
            />
          ))}
        </div>
        <button
          onClick={() => confirmMultiAndAdvance('wedding_functions', 'WEDDING_FUNCTIONS', 'wedding_functions')}
          disabled={selected.length === 0}
          style={{
            background: selected.length === 0 ? PJ.sand : PJ.obsidian,
            color: selected.length === 0 ? PJ.slate : PJ.white,
            border: 'none', borderRadius: '100px', padding: '14px 28px',
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem',
            cursor: selected.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}
        >
          Continue <ChevronRight size={16} />
        </button>
      </QuestionScreen>
    );
  };

  const renderConferenceType = () => (
    <QuestionScreen
      eyebrow="The Event"
      headline="What kind of conference are you planning?"
      stepKey={stepKey}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {CONFERENCE_TYPES.map(ct => (
          <ChoiceCard
            key={ct.value}
            label={ct.label}
            desc={ct.desc}
            selected={journeyState?.conference_type?.value === ct.value}
            onClick={() => selectAndAdvance('conference_type', ct.value)}
          />
        ))}
      </div>
    </QuestionScreen>
  );

  const renderPlanningScope = () => (
    <QuestionScreen
      eyebrow="Your Needs"
      headline="How much would you like us to take care of?"
      stepKey={stepKey}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {PLANNING_SCOPES.map(ps => (
          <ChoiceCard
            key={ps.value}
            label={ps.label}
            desc={ps.desc}
            selected={journeyState?.planning_scope?.value === ps.value}
            onClick={() => {
              const next = {
                ...journeyState,
                planning_scope: { value: ps.value, source: 'CUSTOMER_CONFIRMED', confirmed: true },
              };
              // Set default services for END_TO_END
              if (ps.value === 'END_TO_END') {
                next.service_interests = { value: END_TO_END_DEFAULTS, source: 'SYSTEM_DEFAULT', confirmed: false };
              }
              setJourneyState(next);
              saveJourneyToStorage(next);
              setTimeout(() => {
                const nextStep = getNextStep(next, currentStepId);
                if (nextStep) navigateToStep(nextStep, next);
              }, 320);
            }}
          />
        ))}
      </div>
    </QuestionScreen>
  );

  const renderScopeConfirm = () => {
    const defaults = END_TO_END_DEFAULTS;
    return (
      <QuestionScreen
        eyebrow="Your Needs"
        headline="We'll take care of the essentials."
        helper="Here's what's included by default in end-to-end planning."
        stepKey={stepKey}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '28px' }}>
          {defaults.map(svc => {
            const s = SERVICES.find(x => x.value === svc);
            return (
              <span key={svc} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', fontWeight: 500,
                padding: '8px 14px', borderRadius: '100px',
                background: PJ.blush, color: PJ.obsidian,
                border: `1.5px solid ${PJ.gold}60`,
              }}>
                <Check size={12} style={{ color: PJ.gold }} />
                {s?.label || svc}
              </span>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            data-testid="scope-confirm-looks-good"
            onClick={() => {
              const next = {
                ...journeyState,
                service_interests: { value: END_TO_END_DEFAULTS, source: 'SYSTEM_DEFAULT', confirmed: true },
              };
              setJourneyState(next);
              saveJourneyToStorage(next);
              setTimeout(() => {
                const nextStep = getNextStep(next, currentStepId);
                if (nextStep) navigateToStep(nextStep, next);
              }, 200);
            }}
            style={{
              background: PJ.obsidian, color: PJ.white, border: 'none', borderRadius: '100px',
              padding: '14px 28px', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem',
              cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            Looks Good <Check size={16} />
          </button>
          <button
            data-testid="scope-confirm-customize"
            onClick={() => {
              // Navigate to service selection instead
              const next = { ...journeyState, planning_scope: { value: 'CUSTOM', source: 'CUSTOMER_MODIFIED', confirmed: true } };
              setJourneyState(next);
              saveJourneyToStorage(next);
              navigateToStep('SERVICE_SELECTION', next);
            }}
            style={{
              background: 'none', border: `1.5px solid ${PJ.sand}`, borderRadius: '100px',
              padding: '13px 24px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem',
              color: PJ.slate, cursor: 'pointer', transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = PJ.obsidian; e.currentTarget.style.color = PJ.obsidian; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = PJ.sand; e.currentTarget.style.color = PJ.slate; }}
          >
            Customize
          </button>
        </div>
      </QuestionScreen>
    );
  };

  const renderServiceSelection = () => {
    const selected = multiSelections['SERVICE_SELECTION'] || journeyState?.service_interests?.value || [];
    return (
      <QuestionScreen
        eyebrow="Your Needs"
        headline="What should we help you with?"
        helper="Select all the areas you'd like Prashasta to take care of."
        stepKey={stepKey}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginBottom: '28px' }}>
          {SERVICES.map(svc => {
            const Icon = SERVICE_ICONS[svc.icon];
            return (
              <ChoiceCard
                key={svc.value}
                label={svc.label}
                desc={svc.desc}
                Icon={Icon}
                selected={selected.includes(svc.value)}
                onClick={() => toggleMulti('SERVICE_SELECTION', svc.value)}
              />
            );
          })}
        </div>
        <button
          onClick={() => confirmMultiAndAdvance('service_interests', 'SERVICE_SELECTION', 'service_interests')}
          disabled={selected.length === 0}
          style={{
            background: selected.length === 0 ? PJ.sand : PJ.obsidian,
            color: selected.length === 0 ? PJ.slate : PJ.white,
            border: 'none', borderRadius: '100px', padding: '14px 28px',
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem',
            cursor: selected.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}
        >
          Continue ({selected.length} selected) <ChevronRight size={16} />
        </button>
      </QuestionScreen>
    );
  };

  const renderCity = () => {
    const currentCity = journeyState?.city?.value;
    const showInput = currentCity === 'OTHER';
    return (
      <QuestionScreen
        eyebrow="Your Style"
        headline="Where are you planning the event?"
        stepKey={stepKey}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
          {CITIES.map(city => (
            <button
              key={city.value}
              className="pj-btn-focus pj-choice-card"
              onClick={() => {
                const next = {
                  ...journeyState,
                  city: { value: city.value, display: city.label, source: 'CUSTOMER_CONFIRMED', confirmed: true },
                };
                setJourneyState(next);
                saveJourneyToStorage(next);
                if (city.value !== 'OTHER') {
                  setTimeout(() => {
                    const nextStep = getNextStep(next, currentStepId);
                    if (nextStep) navigateToStep(nextStep, next);
                  }, 320);
                }
              }}
              style={{
                fontFamily: "'DM Sans', sans-serif",
                background: currentCity === city.value ? PJ.blush : PJ.white,
                border: currentCity === city.value ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                borderRadius: '16px', padding: '16px 20px',
                cursor: 'pointer', textAlign: 'center',
                fontWeight: currentCity === city.value ? 600 : 400,
                fontSize: '0.9rem', color: currentCity === city.value ? PJ.obsidian : PJ.slate,
                transition: 'all 0.15s ease',
              }}
            >
              {city.label}
            </button>
          ))}
        </div>
        {showInput && (
          <div style={{ marginTop: '16px', animation: 'pjFadeSlideIn 0.3s ease both' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Enter your city"
                value={cityInput}
                onChange={e => setCityInput(e.target.value)}
                autoFocus
                style={{
                  flex: 1, border: `1.5px solid ${PJ.sand}`, borderRadius: '12px',
                  padding: '12px 16px', fontFamily: "'DM Sans', sans-serif",
                  fontSize: '0.9rem', color: PJ.obsidian, outline: 'none',
                  background: PJ.white,
                }}
                onFocus={e => { e.target.style.borderColor = PJ.gold; }}
                onBlur={e => { e.target.style.borderColor = PJ.sand; }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && cityInput.trim()) {
                    const next = { ...journeyState, city: { value: 'OTHER', display: cityInput.trim(), source: 'CUSTOMER_CONFIRMED', confirmed: true }, city_custom: cityInput.trim() };
                    setJourneyState(next);
                    saveJourneyToStorage(next);
                    const nextStep = getNextStep(next, currentStepId);
                    if (nextStep) navigateToStep(nextStep, next);
                  }
                }}
              />
              <button
                onClick={() => {
                  if (!cityInput.trim()) return;
                  const next = { ...journeyState, city: { value: 'OTHER', display: cityInput.trim(), source: 'CUSTOMER_CONFIRMED', confirmed: true }, city_custom: cityInput.trim() };
                  setJourneyState(next);
                  saveJourneyToStorage(next);
                  const nextStep = getNextStep(next, currentStepId);
                  if (nextStep) navigateToStep(nextStep, next);
                }}
                style={{ background: PJ.obsidian, color: PJ.white, border: 'none', borderRadius: '12px', padding: '12px 20px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem' }}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </QuestionScreen>
    );
  };

  const renderStylePref = () => {
    const selected = multiSelections['STYLE_PREF'] || journeyState?.style_preferences?.value || [];
    const MAX_STYLE = 3;
    return (
      <QuestionScreen
        eyebrow="Your Style"
        headline="What kind of atmosphere are you imagining?"
        helper={`Choose up to ${MAX_STYLE} that feel right for your event.`}
        stepKey={stepKey}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '28px' }}>
          {STYLE_OPTIONS.map(s => (
            <StyleChip
              key={s.value}
              label={s.label}
              selected={selected.includes(s.value)}
              disabled={!selected.includes(s.value) && selected.length >= MAX_STYLE}
              onClick={() => toggleMulti('STYLE_PREF', s.value, MAX_STYLE)}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => confirmMultiAndAdvance('style_preferences', 'STYLE_PREF', 'style_preferences')}
            disabled={selected.length === 0}
            style={{
              background: selected.length === 0 ? PJ.sand : PJ.obsidian,
              color: selected.length === 0 ? PJ.slate : PJ.white,
              border: 'none', borderRadius: '100px', padding: '14px 28px',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem',
              cursor: selected.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            Continue <ChevronRight size={16} />
          </button>
          <button
            onClick={() => {
              const next = updateState({ style_preferences: { value: [], source: 'CUSTOMER_SKIPPED', confirmed: false } });
              advance(next);
            }}
            style={{ background: 'none', border: `1.5px solid ${PJ.sand}`, borderRadius: '100px', padding: '12px 20px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: PJ.slate, cursor: 'pointer' }}
          >
            Skip for now
          </button>
        </div>
      </QuestionScreen>
    );
  };

  const renderGuestCount = () => (
    <QuestionScreen
      eyebrow="The Details"
      headline="How many people are joining you?"
      stepKey={stepKey}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
        {GUEST_RANGES.map(gr => (
          <button
            key={gr.value}
            className="pj-btn-focus pj-choice-card"
            onClick={() => selectAndAdvance('guest_count_range', gr.value, {
              guest_count_range: { value: gr.value, display: gr.display, source: 'CUSTOMER_CONFIRMED', confirmed: true }
            })}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              background: journeyState?.guest_count_range?.value === gr.value ? PJ.blush : PJ.white,
              border: journeyState?.guest_count_range?.value === gr.value ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
              borderRadius: '16px', padding: '16px 20px', cursor: 'pointer', textAlign: 'center',
              fontWeight: journeyState?.guest_count_range?.value === gr.value ? 600 : 400,
              fontSize: '0.9rem', color: journeyState?.guest_count_range?.value === gr.value ? PJ.obsidian : PJ.slate,
              transition: 'all 0.15s ease',
            }}
          >
            {gr.label}
          </button>
        ))}
      </div>
    </QuestionScreen>
  );

  const renderVenueStatus = () => (
    <QuestionScreen
      eyebrow="The Details"
      headline="Have you found the venue yet?"
      stepKey={stepKey}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {VENUE_STATUSES.map(vs => (
          <ChoiceCard
            key={vs.value}
            label={vs.label}
            desc={vs.desc}
            selected={journeyState?.venue_status?.value === vs.value}
            onClick={() => selectAndAdvance('venue_status', vs.value)}
          />
        ))}
      </div>
    </QuestionScreen>
  );

  const renderDate = () => {
    const minDate = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })();
    const maxDate = (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 3); return d.toISOString().split('T')[0]; })();
    return (
      <QuestionScreen
        eyebrow="The Details"
        headline="When are you thinking?"
        stepKey={stepKey}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
          {[
            { value: 'EXACT', label: 'I know the exact date' },
            { value: 'MONTH', label: 'I know the month' },
            { value: 'DECIDING', label: 'Still deciding' },
          ].map(opt => {
            const isSelected = datePrecision === opt.value;
            return (
              <button
                key={opt.value}
                className="pj-btn-focus pj-choice-card"
                data-testid={`date-precision-${opt.value.toLowerCase()}`}
                onClick={() => {
                  setDatePrecision(opt.value);
                  if (opt.value === 'DECIDING') {
                    const next = updateState({ event_date: { value: null, source: 'CUSTOMER_UNDECIDED', confirmed: false } });
                    advance(next);
                  }
                }}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: isSelected ? `${PJ.gold}18` : PJ.white,
                  border: `1.5px solid ${isSelected ? PJ.gold : PJ.sand}`,
                  borderRadius: '16px',
                  padding: '16px 20px', cursor: 'pointer', textAlign: 'left', width: '100%',
                  fontSize: '0.9rem',
                  color: isSelected ? PJ.obsidian : PJ.obsidian,
                  transition: 'all 0.15s',
                  fontWeight: isSelected ? 600 : 400,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: '8px' }}>
          <label style={{ display: 'block', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: PJ.slate, marginBottom: '8px', letterSpacing: '0.04em' }}>
            Or pick a date:
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', border: `1.5px solid ${PJ.sand}`, borderRadius: '12px', padding: '12px 14px', background: PJ.white }}>
              <Calendar size={16} style={{ color: PJ.gold, flexShrink: 0 }} />
              <input
                type="date"
                value={dateInput}
                min={minDate}
                max={maxDate}
                onChange={e => setDateInput(e.target.value)}
                data-testid="journey-date-input"
                style={{ flex: 1, border: 'none', background: 'transparent', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: PJ.obsidian, outline: 'none', cursor: 'pointer' }}
              />
            </div>
            <button
              data-testid="date-advance-btn"
              onClick={() => {
                if (!dateInput) return;
                const next = updateState({ event_date: { value: dateInput, display: formatEventDateShort(dateInput), source: 'CUSTOMER_CONFIRMED', confirmed: true } });
                advance(next);
              }}
              disabled={!dateInput}
              style={{
                background: dateInput ? PJ.obsidian : PJ.sand, color: dateInput ? PJ.white : PJ.slate,
                border: 'none', borderRadius: '12px', padding: '12px 20px',
                cursor: dateInput ? 'pointer' : 'not-allowed',
                fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
              }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </QuestionScreen>
    );
  };

  // ── GQP-03: renderEstimate — Your Event Quote Planner step ──────────────────
  const renderEstimate = () => {
    const status = estimateState.status;
    const data = estimateState.data;
    const stale = isEstimateStale();
    const guestCountNum = parseInt(estimateGuestCount, 10);
    const hasDate = !!journeyState?.event_date?.value;
    const hasServices = (journeyState?.service_interests?.value || []).length > 0;
    const canCalculate = guestCountNum > 0 && hasDate && hasServices;
    const showResults = (status === 'success' || status === 'error' || status === 'unavailable') && !stale;

    return (
      <QuestionScreen
        eyebrow="Your Plan"
        headline="Your Indicative Event Estimate"
        helper="We'll calculate a transparent estimate based on your selections. Your planner will confirm final pricing."
        stepKey={stepKey}
      >
        <div style={{ maxWidth: '540px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* ── Guest Count Input ── */}
          <div data-testid="estimate-guest-count-section">
            <label style={{
              display: 'block', fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.78rem', fontWeight: 600, color: PJ.slate,
              marginBottom: '8px', letterSpacing: '0.02em',
            }}>
              Expected guest count
            </label>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              border: `1.5px solid ${PJ.sand}`, borderRadius: '12px',
              padding: '12px 14px', background: PJ.white, transition: 'border-color 0.15s',
            }}
              onFocusCapture={e => { e.currentTarget.style.borderColor = PJ.gold; }}
              onBlurCapture={e => { e.currentTarget.style.borderColor = PJ.sand; }}
            >
              <Users size={16} style={{ color: PJ.gold, flexShrink: 0 }} />
              <input
                type="number"
                min="1"
                max="10000"
                value={estimateGuestCount}
                onChange={e => {
                  setEstimateGuestCount(e.target.value);
                  if (estimateState.status === 'success') {
                    setEstimateState(s => ({ ...s, status: 'stale' }));
                  }
                }}
                placeholder="e.g. 150"
                data-testid="estimate-guest-count-input"
                style={{
                  flex: 1, border: 'none', background: 'transparent',
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem',
                  color: PJ.obsidian, outline: 'none',
                }}
              />
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: PJ.slate }}>guests</span>
            </div>
            {journeyState?.guest_count_range?.display && (
              <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif" }}>
                You selected <strong>{journeyState.guest_count_range.display}</strong> guests — enter an exact count for a more precise estimate.
              </p>
            )}
          </div>

          {/* ── Missing date notice ── */}
          {!hasDate && (
            <div data-testid="estimate-no-date-notice" style={{
              background: `${PJ.gold}15`, border: `1px solid ${PJ.gold}40`,
              borderRadius: '10px', padding: '12px 14px',
              display: 'flex', gap: '8px', alignItems: 'flex-start',
            }}>
              <AlertCircle size={15} style={{ color: PJ.gold, flexShrink: 0, marginTop: '1px' }} />
              <p style={{ margin: 0, fontSize: '0.82rem', color: PJ.obsidian, fontFamily: "'DM Sans', sans-serif" }}>
                Please go back and select an event date to enable pricing.
              </p>
            </div>
          )}

          {/* ── Stale indicator ── */}
          {stale && status === 'success' && (
            <div data-testid="estimate-stale-notice" style={{
              background: `#FFF3CD`, border: `1px solid #FFC107`,
              borderRadius: '10px', padding: '10px 14px',
              display: 'flex', gap: '8px', alignItems: 'center',
            }}>
              <RefreshCw size={14} style={{ color: '#856404' }} />
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#856404', fontFamily: "'DM Sans', sans-serif" }}>
                Your selections have changed. Recalculate to update your estimate.
              </p>
            </div>
          )}

          {/* ── Calculate Button ── */}
          <button
            data-testid="estimate-calculate-btn"
            onClick={handleCalculateEstimate}
            disabled={!canCalculate || status === 'loading'}
            style={{
              background: !canCalculate || status === 'loading' ? PJ.sand : PJ.obsidian,
              color: !canCalculate || status === 'loading' ? PJ.slate : PJ.white,
              border: 'none', borderRadius: '100px', padding: '14px 24px',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem',
              cursor: !canCalculate || status === 'loading' ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              transition: 'all 0.2s',
            }}
          >
            {status === 'loading' ? (
              <>
                <div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'pjSpin 0.8s linear infinite' }} />
                Calculating your estimate…
              </>
            ) : stale || status === 'stale' ? (
              <><RefreshCw size={15} /> Recalculate Estimate</>
            ) : status === 'success' ? (
              <><RefreshCw size={15} /> Recalculate</>
            ) : (
              <><Calculator size={15} /> Calculate My Estimate</>
            )}
          </button>

          {/* ── Estimate Results ── */}
          {showResults && data && (
            <div data-testid="estimate-results-card" style={{
              background: PJ.white, borderRadius: '16px',
              border: `1px solid ${PJ.sand}`, overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(32,27,25,0.06)',
            }}>
              {/* Incomplete pricing banner */}
              {data.has_incomplete_pricing && (
                <div data-testid="estimate-incomplete-banner" style={{
                  background: '#FFF3CD', padding: '10px 16px',
                  display: 'flex', gap: '8px', alignItems: 'flex-start',
                  borderBottom: `1px solid ${PJ.sand}`,
                }}>
                  <AlertCircle size={14} style={{ color: '#856404', flexShrink: 0, marginTop: '1px' }} />
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#856404', fontFamily: "'DM Sans', sans-serif" }}>
                    This estimate covers services where pricing is configured. Additional items are to be confirmed by your planner.
                  </p>
                </div>
              )}

              {/* Service Breakdowns */}
              <div style={{ padding: '16px' }}>
                <p style={{ margin: '0 0 12px', fontSize: '0.72rem', fontWeight: 700, color: PJ.slate, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'DM Sans', sans-serif" }}>
                  Service Estimates
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {(data.service_breakdowns || []).map((bd, i) => (
                    <div key={i} data-testid={`estimate-line-${i}`} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                      padding: '10px 0',
                      borderBottom: i < (data.service_breakdowns.length - 1) ? `1px solid ${PJ.sand}50` : 'none',
                    }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '0.88rem', color: PJ.obsidian, fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                          {bd.service_name}
                        </p>
                        {bd.pricing_note && (
                          <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif" }}>
                            {bd.pricing_note}
                          </p>
                        )}
                      </div>
                      <span style={{
                        fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', fontWeight: 600,
                        color: PJ.obsidian, whiteSpace: 'nowrap', marginLeft: '12px',
                      }}>
                        {fmtINR(bd.line_total_minor)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div style={{ background: PJ.ivory, padding: '14px 16px', borderTop: `1px solid ${PJ.sand}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.slate }}>Subtotal</span>
                  <span data-testid="estimate-subtotal" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.obsidian }}>
                    {fmtINR(data.subtotal_minor)}
                  </span>
                </div>
                {data.show_tax_line && data.tax_total_minor > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.slate }}>Taxes & Charges</span>
                    <span data-testid="estimate-tax" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.obsidian }}>
                      {fmtINR(data.tax_total_minor)}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px', borderTop: `1px solid ${PJ.sand}` }}>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', fontWeight: 700, color: PJ.obsidian }}>Estimated Total</span>
                  <span data-testid="estimate-total" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.2rem', fontWeight: 600, color: PJ.ruby }}>
                    {fmtINR(data.total_priced_minor)}
                  </span>
                </div>
                {data.show_range && data.range_minimum_minor && data.range_maximum_minor && (
                  <div data-testid="estimate-range" style={{
                    marginTop: '10px', padding: '8px 10px',
                    background: `${PJ.gold}12`, borderRadius: '8px',
                    border: `1px solid ${PJ.gold}25`,
                  }}>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif" }}>
                      Indicative range: <strong style={{ color: PJ.obsidian }}>{fmtINR(data.range_minimum_minor)}</strong> — <strong style={{ color: PJ.obsidian }}>{fmtINR(data.range_maximum_minor)}</strong>
                    </p>
                  </div>
                )}
              </div>

              {/* Price on Request items */}
              {(data.price_on_request_items || []).length > 0 && (
                <div data-testid="estimate-por-section" style={{
                  padding: '12px 16px', borderTop: `1px solid ${PJ.sand}`,
                }}>
                  <p style={{ margin: '0 0 8px', fontSize: '0.72rem', fontWeight: 700, color: PJ.slate, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'DM Sans', sans-serif" }}>
                    Price on Request
                  </p>
                  {data.price_on_request_items.map((item, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: PJ.obsidian }}>{item.service_name || item}</span>
                      <span style={{
                        fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 600,
                        color: PJ.slate, background: PJ.sand, borderRadius: '4px', padding: '2px 8px',
                      }}>
                        Price on Request
                      </span>
                    </div>
                  ))}
                  <p style={{ margin: '8px 0 0', fontSize: '0.72rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif" }}>
                    Your planner will confirm pricing for these items.
                  </p>
                </div>
              )}

              {/* Missing pricing items */}
              {(data.pricing_missing || []).length > 0 && (
                <div data-testid="estimate-missing-section" style={{
                  padding: '12px 16px', borderTop: `1px solid ${PJ.sand}`,
                  background: '#FFF9F0',
                }}>
                  <p style={{ margin: '0 0 4px', fontSize: '0.72rem', fontWeight: 700, color: '#856404', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'DM Sans', sans-serif" }}>
                    Pricing to be confirmed
                  </p>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#856404', fontFamily: "'DM Sans', sans-serif" }}>
                    {data.pricing_missing.join(', ')} — your planner will include these in a personalised quote.
                  </p>
                </div>
              )}

              {/* Footer: assumptions + valid until + disclaimer */}
              <div style={{ padding: '12px 16px', borderTop: `1px solid ${PJ.sand}`, background: PJ.ivory }}>
                {/* Calculation assumptions */}
                {(data.calculation_assumptions || []).length > 0 && (
                  <div data-testid="estimate-assumptions" style={{ marginBottom: '8px' }}>
                    {data.calculation_assumptions.map((a, i) => (
                      <p key={i} style={{ margin: '0 0 3px', fontSize: '0.72rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif" }}>
                        <Info size={10} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{a}
                      </p>
                    ))}
                  </div>
                )}
                {data.valid_until && (
                  <p data-testid="estimate-valid-until" style={{ margin: '0 0 6px', fontSize: '0.75rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif" }}>
                    Valid until <strong>{new Date(data.valid_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                  </p>
                )}
                {data.estimate_ref && (
                  <p data-testid="estimate-ref-display" style={{ margin: '0 0 6px', fontSize: '0.72rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif" }}>
                    Reference: <span style={{ fontWeight: 600, letterSpacing: '0.04em' }}>{data.estimate_ref}</span>
                  </p>
                )}
                {data.disclaimer_text && (
                  <p style={{ margin: 0, fontSize: '0.72rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }}>
                    <Info size={11} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    {data.disclaimer_text}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── API Error / Unavailable state ── */}
          {(status === 'error' || status === 'unavailable') && (
            <div data-testid="estimate-error-notice" style={{
              background: PJ.white, border: `1px solid ${PJ.sand}`,
              borderRadius: '12px', padding: '16px',
            }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <AlertCircle size={16} style={{ color: PJ.gold, flexShrink: 0, marginTop: '1px' }} />
                <div>
                  <p style={{ margin: '0 0 6px', fontSize: '0.88rem', fontWeight: 600, color: PJ.obsidian, fontFamily: "'DM Sans', sans-serif" }}>
                    Estimate unavailable
                  </p>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }}>
                    {estimateState.error || 'Your planner will prepare a personalised quote after reviewing your requirements.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Preferred Budget (optional) ── */}
          <div data-testid="estimate-budget-section">
            <label style={{
              display: 'block', fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.78rem', fontWeight: 600, color: PJ.slate,
              marginBottom: '8px', letterSpacing: '0.02em',
            }}>
              Preferred budget <span style={{ fontWeight: 400, color: PJ.slate }}>(optional)</span>
            </label>
            <p style={{ margin: '0 0 10px', fontSize: '0.78rem', color: PJ.slate, fontFamily: "'DM Sans', sans-serif" }}>
              This helps your planner understand your expectations — it won't affect the calculated estimate above.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {BUDGET_RANGES.map(br => (
                <button
                  key={br.value}
                  data-testid={`budget-option-${br.value}`}
                  onClick={() => setEstimateBudgetPref(br.value === estimateBudgetPref ? '' : br.value)}
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    background: estimateBudgetPref === br.value ? PJ.blush : PJ.white,
                    border: estimateBudgetPref === br.value ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                    borderRadius: '12px', padding: '10px 14px', cursor: 'pointer', textAlign: 'center',
                    fontWeight: estimateBudgetPref === br.value ? 600 : 400,
                    fontSize: '0.82rem', color: estimateBudgetPref === br.value ? PJ.obsidian : PJ.slate,
                    transition: 'all 0.15s ease',
                  }}
                >
                  {br.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Continue Button ── */}
          <button
            data-testid="estimate-continue-btn"
            onClick={() => {
              // Save budget preference to journey state before advancing
              const next = updateState({
                budget_range: estimateBudgetPref
                  ? { value: estimateBudgetPref, display: BUDGET_RANGES.find(b => b.value === estimateBudgetPref)?.label || estimateBudgetPref, source: 'CUSTOMER_CONFIRMED', confirmed: true }
                  : journeyState.budget_range,
                guest_count_exact: guestCountNum || undefined,
              });
              advance(next);
            }}
            style={{
              background: PJ.ruby, color: PJ.white,
              border: 'none', borderRadius: '100px', padding: '16px 28px',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.95rem',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            {status === 'success' ? 'Continue with this estimate' : 'Continue'} <ArrowRight size={16} />
          </button>

        </div>
      </QuestionScreen>
    );
  };

  const renderBudget = () => renderEstimate(); // backward compat — old saved drafts with BUDGET step

  const renderContact = () => (
    <QuestionScreen
      eyebrow="Your Plan"
      headline="Your Event Plan is ready."
      helper="Save your plan and continue with Prashasta whenever you're ready."
      stepKey={stepKey}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '420px' }}>
        {[
          { label: 'Your name', key: 'name', type: 'text', icon: User, placeholder: 'e.g. Priya Sharma', testid: 'contact-name' },
          { label: 'Mobile number', key: 'mobile', type: 'tel', icon: Phone, placeholder: '+91 XXXXX XXXXX', testid: 'contact-mobile' },
          { label: 'Email (optional)', key: 'email', type: 'email', icon: Mail, placeholder: 'your@email.com', testid: 'contact-email', optional: true },
        ].map(({ label, key, type, icon: Icon, placeholder, testid, optional }) => (
          <div key={key}>
            <label style={{ display: 'block', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: PJ.slate, marginBottom: '8px', letterSpacing: '0.02em' }}>
              {label}
            </label>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              border: `1.5px solid ${PJ.sand}`, borderRadius: '12px', padding: '12px 14px', background: PJ.white,
              transition: 'border-color 0.15s',
            }}
              onFocusCapture={e => { e.currentTarget.style.borderColor = PJ.gold; }}
              onBlurCapture={e => { e.currentTarget.style.borderColor = PJ.sand; }}
            >
              <Icon size={16} style={{ color: PJ.gold, flexShrink: 0 }} />
              <input
                type={type} value={contactForm[key]}
                onChange={e => setContactForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                data-testid={testid}
                style={{ flex: 1, border: 'none', background: 'transparent', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: PJ.obsidian, outline: 'none' }}
              />
            </div>
          </div>
        ))}

        <button
          data-testid="save-event-plan-btn"
          onClick={() => {
            if (!contactForm.name || !contactForm.mobile) return;
            const next = updateState({
              contact: contactForm,
              journey_completion: 100,
            });
            advance(next);
          }}
          disabled={!contactForm.name || !contactForm.mobile}
          style={{
            marginTop: '8px',
            background: !contactForm.name || !contactForm.mobile ? PJ.sand : PJ.ruby,
            color: !contactForm.name || !contactForm.mobile ? PJ.slate : PJ.white,
            border: 'none', borderRadius: '100px', padding: '16px 28px',
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.95rem',
            cursor: !contactForm.name || !contactForm.mobile ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { if (contactForm.name && contactForm.mobile) e.currentTarget.style.opacity = '0.9'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          Save My Event Plan <ArrowRight size={17} />
        </button>
      </div>
    </QuestionScreen>
  );

  const renderResult = () => {
    const name      = journeyState?.contact?.name || '';
    const firstName = name.split(' ')[0];
    const eventType = EVENT_TYPES.find(e => e.value === journeyState?.event_type?.value)?.label || journeyState?.event_type?.label || '';
    const city      = getCityDisplay(journeyState);
    const gcrDisplay = journeyState?.guest_count_range?.display || GUEST_RANGES.find(g => g.value === journeyState?.guest_count_range?.value)?.display;
    const guests    = gcrDisplay;
    const styleValues = journeyState?.style_preferences?.value || [];
    const styleLabels = styleValues.map(v => STYLE_OPTIONS.find(s => s.value === v)?.label || v);
    const services  = journeyState?.service_interests?.value || [];
    const scope     = journeyState?.planning_scope?.value;
    const budget    = journeyState?.budget_range?.display || BUDGET_RANGES.find(b => b.value === journeyState?.budget_range?.value)?.display;
    const date      = journeyState?.event_date?.display || (journeyState?.event_date?.value ? formatEventDateShort(journeyState.event_date.value) : '');
    const mobile    = journeyState?.contact?.mobile || '';
    const email     = journeyState?.contact?.email || '';
    const isSubmitting = submitState.status === 'submitting';
    const isSubmitted  = submitState.status === 'submitted';
    const isError      = submitState.status === 'error';

    // ── Shared event plan summary card (used in both pre/post submission) ──────
    const EventSummaryCard = ({ readOnly = false }) => (
      <div
        data-testid="event-plan-card"
        style={{
          background: PJ.white, borderRadius: '28px', overflow: 'hidden',
          boxShadow: '0 16px 60px -8px rgba(32,27,25,0.16)',
          border: `1px solid ${PJ.sand}`,
        }}
      >
        <div style={{ background: PJ.obsidian, padding: '32px', textAlign: 'center', position: 'relative' }}>
          {readOnly && (
            <span style={{
              position: 'absolute', top: '14px', right: '16px',
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.6rem', fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase', color: '#4ADE80',
              background: 'rgba(74,222,128,0.12)', borderRadius: '100px', padding: '4px 10px',
            }}>
              Submitted
            </span>
          )}
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: PJ.gold, margin: '0 0 8px' }}>
            Prashasta Event Intelligence
          </p>
          {firstName ? (
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', color: PJ.white, margin: '0 0 6px', letterSpacing: '0.04em' }}>
              {firstName}'s {eventType}
            </h2>
          ) : (
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: '2rem', color: PJ.white, margin: '0 0 6px' }}>
              {eventType}
            </h2>
          )}
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: `${PJ.white}90`, margin: 0 }}>
            {city}{date ? ` · ${date}` : ''}
          </p>
        </div>

        <div style={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {guests && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Users size={16} style={{ color: PJ.gold }} />
              <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: PJ.obsidian }}>
                <strong>{guests}</strong> guests
              </p>
            </div>
          )}

          {styleLabels.length > 0 && (
            <div>
              <p style={{ margin: '0 0 8px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: PJ.slate }}>
                Your Direction
              </p>
              <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem', fontStyle: 'italic', color: PJ.obsidian }}>
                {styleLabels.join(' · ')}
              </p>
            </div>
          )}

          <div>
            <p style={{ margin: '0 0 10px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: PJ.slate }}>
              {scope === 'END_TO_END' ? 'End-to-End Planning' : 'Selected Services'}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {services.map(s => (
                <span key={s} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', padding: '5px 12px', borderRadius: '100px', background: PJ.ivory, border: `1px solid ${PJ.sand}`, color: PJ.obsidian }}>
                  {getServiceLabel(s)}
                </span>
              ))}
            </div>
          </div>

          {budget && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '4px', borderTop: `1px solid ${PJ.sand}` }}>
              <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: PJ.slate }}>
                Investment: <strong style={{ color: PJ.obsidian }}>{budget}</strong>
              </p>
            </div>
          )}

          {/* Contact details — required by P0 review screen spec */}
          <div style={{ paddingTop: '16px', borderTop: `1px solid ${PJ.sand}`, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: PJ.slate }}>
              Your Contact Details
            </p>
            {name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={14} style={{ color: PJ.gold, flexShrink: 0 }} />
                <p data-testid="review-contact-name" style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: PJ.obsidian }}>{name}</p>
              </div>
            )}
            {mobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Phone size={14} style={{ color: PJ.gold, flexShrink: 0 }} />
                <p data-testid="review-contact-mobile" style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: PJ.obsidian }}>{mobile}</p>
              </div>
            )}
            {email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Mail size={14} style={{ color: PJ.gold, flexShrink: 0 }} />
                <p data-testid="review-contact-email" style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: PJ.obsidian }}>{email}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );

    // ── P0: Confirmation screen (post-submission) ──────────────────────────────
    if (isSubmitted) {
      return (
        <div key={stepKey} className="pj-step-enter" style={{ width: '100%', maxWidth: '560px' }}>
          {/* Confirmation Card */}
          <div
            data-testid="submission-confirmation-card"
            style={{
              background: PJ.white, borderRadius: '28px', overflow: 'hidden',
              boxShadow: '0 16px 60px -8px rgba(32,27,25,0.16)',
              border: `1px solid ${PJ.sand}`, marginBottom: '20px',
            }}
          >
            {/* Green success header */}
            <div style={{ background: '#0a4d2c', padding: '40px 32px', textAlign: 'center' }}>
              <div style={{
                width: '58px', height: '58px', borderRadius: '50%',
                background: 'rgba(74,222,128,0.18)', border: '1.5px solid rgba(74,222,128,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 18px',
              }}>
                <Check size={26} style={{ color: '#4ADE80' }} />
              </div>
              <h2 style={{
                fontFamily: "'Cormorant Garamond', serif", fontWeight: 400,
                fontSize: 'clamp(1.35rem, 4vw, 1.85rem)', color: PJ.white,
                margin: '0 0 8px', letterSpacing: '0.02em',
              }}>
                Your Event Request Has Been Received!
              </h2>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', margin: 0 }}>
                {firstName ? `${firstName}, thank` : 'Thank'} you for choosing Prashasta Events
              </p>
            </div>

            <div style={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: PJ.slate, lineHeight: 1.75 }}>
                Our relationship management team will review your event details and contact you soon to discuss your requirements and next steps.
              </p>

              {submitState.reference && (
                <div
                  data-testid="public-reference-block"
                  style={{
                    background: PJ.ivory, borderRadius: '14px', padding: '18px 20px',
                    border: `1px solid ${PJ.sand}`,
                  }}
                >
                  <p style={{ margin: '0 0 6px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: PJ.slate }}>
                    Your Request Reference
                  </p>
                  <p data-testid="submission-reference" style={{ margin: '0 0 6px', fontFamily: "'DM Sans', sans-serif", fontSize: '1.1rem', fontWeight: 700, color: PJ.obsidian, letterSpacing: '0.08em' }}>
                    {submitState.reference}
                  </p>
                  <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: PJ.slate }}>
                    Please save this reference — our team will use it to identify your request.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Post-submission actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            <button
              data-testid="confirmation-whatsapp-btn"
              onClick={() => setWhatsappOpen(true)}
              style={{
                background: '#25D366', color: PJ.white, border: 'none', borderRadius: '100px',
                padding: '15px 28px', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              <MessageCircle size={17} />
              WhatsApp Our Team
            </button>

            <button
              data-testid="review-submitted-details-btn"
              onClick={() => setShowSubmittedDetails(v => !v)}
              style={{
                background: 'none', border: `1.5px solid ${PJ.sand}`, borderRadius: '100px',
                padding: '13px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem',
                color: PJ.slate, cursor: 'pointer', transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = PJ.obsidian; e.currentTarget.style.color = PJ.obsidian; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = PJ.sand; e.currentTarget.style.color = PJ.slate; }}
            >
              {showSubmittedDetails ? 'Hide Submitted Details' : 'Review Submitted Details'}
              {showSubmittedDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            <button
              data-testid="start-new-plan-btn"
              onClick={handleStartNewPlan}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.slate,
                padding: '8px', textAlign: 'center', textDecoration: 'underline',
                textDecorationColor: PJ.sand,
              }}
            >
              Start New Event Plan
            </button>
          </div>

          {/* Read-only submitted plan details (toggled) */}
          {showSubmittedDetails && (
            <div style={{ animation: 'pjFadeSlideIn 0.25s ease both' }}>
              <EventSummaryCard readOnly />
            </div>
          )}
        </div>
      );
    }

    // ── P0: Review screen + consent + explicit submit (pre-submission) ─────────
    return (
      <div key={stepKey} className="pj-step-enter" style={{ width: '100%', maxWidth: '560px' }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: PJ.gold, margin: '0 0 20px' }}>
          Review Your Event Request
        </p>

        <div style={{ marginBottom: '20px' }}>
          <EventSummaryCard />
        </div>

        {/* Consent section */}
        <div
          data-testid="consent-section"
          style={{
            background: PJ.white, borderRadius: '16px', padding: '20px 22px',
            border: `1px solid ${PJ.sand}`, marginBottom: '16px',
            display: 'flex', flexDirection: 'column', gap: '14px',
          }}
        >
          {/* GQP-LUX-02: Callback preference */}
          {renderCallbackPreference()}

          {/* Required contact consent */}
          <label
            data-testid="consent-checkbox-label"
            style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', cursor: 'pointer' }}
          >
            <input
              data-testid="consent-checkbox"
              type="checkbox"
              checked={consentGiven}
              onChange={e => setConsentGiven(e.target.checked)}
              style={{ marginTop: '3px', accentColor: PJ.ruby, width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer' }}
            />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.obsidian, lineHeight: 1.65 }}>
              I agree that Prashasta Events may contact me regarding my event request by phone, WhatsApp or email, using the contact details I provided.{' '}
              <a
                href="https://prashastaevents.com/privacy-notice"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="privacy-notice-link"
                style={{ color: PJ.gold, textDecoration: 'underline' }}
                onClick={e => e.stopPropagation()}
              >
                Privacy Notice
              </a>
            </span>
          </label>

          {/* Optional marketing consent (separate) */}
          <label
            data-testid="marketing-consent-label"
            style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', cursor: 'pointer' }}
          >
            <input
              data-testid="marketing-consent-checkbox"
              type="checkbox"
              checked={marketingConsent}
              onChange={e => setMarketingConsent(e.target.checked)}
              style={{ marginTop: '3px', accentColor: PJ.ruby, width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer' }}
            />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.slate, lineHeight: 1.65 }}>
              I would also like to receive event inspiration, ideas and updates from Prashasta Events.{' '}
              <span style={{ fontSize: '0.75rem', color: PJ.slate, opacity: 0.7 }}>(Optional)</span>
            </span>
          </label>
        </div>

        {/* Error banner */}
        {isError && (
          <div
            data-testid="submit-error-banner"
            style={{
              background: '#FFF0F3', border: `1px solid ${PJ.ruby}40`, borderRadius: '12px',
              padding: '12px 16px', marginBottom: '12px',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.ruby }}>
              Unable to submit your request. Please try again, or connect with us via WhatsApp.
            </p>
          </div>
        )}

        {/* Submit actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            data-testid="submit-event-request-btn"
            onClick={handleSubmitRequest}
            disabled={!consentGiven || isSubmitting}
            style={{
              background: (!consentGiven || isSubmitting) ? PJ.sand : PJ.ruby,
              color: (!consentGiven || isSubmitting) ? PJ.slate : PJ.white,
              border: 'none', borderRadius: '100px', padding: '16px 28px',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.95rem',
              cursor: (!consentGiven || isSubmitting) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              transition: 'all 0.2s', opacity: 1,
            }}
            onMouseEnter={e => { if (consentGiven && !isSubmitting) e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            {isSubmitting ? (
              <>
                <span style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.35)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'pjSpin 0.8s linear infinite' }} />
                Submitting...
              </>
            ) : (
              <>Submit My Event Request <ArrowRight size={17} /></>
            )}
          </button>

          <button
            data-testid="result-edit-btn"
            onClick={() => navigateToStep('EVENT_TYPE')}
            disabled={isSubmitting}
            style={{
              background: 'none', border: `1.5px solid ${PJ.sand}`, borderRadius: '100px',
              padding: '13px', fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.85rem', color: PJ.slate,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s', opacity: isSubmitting ? 0.5 : 1,
            }}
            onMouseEnter={e => { if (!isSubmitting) { e.currentTarget.style.borderColor = PJ.obsidian; e.currentTarget.style.color = PJ.obsidian; }}}
            onMouseLeave={e => { e.currentTarget.style.borderColor = PJ.sand; e.currentTarget.style.color = PJ.slate; }}
          >
            Edit My Plan
          </button>

          <button
            onClick={() => navigate('/event-journey')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.slate,
              padding: '8px', textAlign: 'center', textDecoration: 'underline',
              textDecorationColor: PJ.sand,
            }}
          >
            Explore Our Work
          </button>
        </div>
      </div>
    );
  };

  // ── GQP-LUX-01: Load luxury estimate ────────────────────────────────────────
  const handleLoadLuxuryEstimate = async (state = luxuryState) => {
    if (!state.eventType || !state.city || !state.date || !state.guestCount) return;
    setLuxuryLoading(true);
    setLuxuryError(null);
    try {
      const { data } = await publicLuxApi.getLuxuryEstimate({
        alias: 'prashasta',
        event_type: state.eventType,
        location_city: state.city,
        event_date: state.date,
        guest_count: parseInt(state.guestCount, 10),
        selected_functions: state.functions,
        disabled_service_codes: state.disabledServices,
        currency: 'INR',
      });
      setLuxuryEstimate(data);
    } catch (err) {
      setLuxuryError(
        err?.response?.data?.detail || 'Unable to load luxury estimate. Please try again.'
      );
    } finally {
      setLuxuryLoading(false);
    }
  };

  // ── GQP-LUX-01: Mode selection screen ────────────────────────────────────────
  const renderModeSelect = () => (
    <div className="pj-step-enter" style={{ maxWidth: 560 }} data-testid="mode-select-screen">
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: PJ.gold, marginBottom: 10 }}>
          Prashasta Events
        </p>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.7rem,4vw,2.2rem)', fontWeight: 600, color: PJ.obsidian, margin: '0 0 8px' }}>
          How would you like to plan your event?
        </h2>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: PJ.slate, margin: 0 }}>
          Choose your planning experience.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Luxury Card */}
        <button
          data-testid="luxury-mode-btn"
          onClick={() => {
            setJourneyMode('LUXURY');
            const savedJourney = loadJourneyFromStorage() || {};
            saveJourneyToStorage({ ...savedJourney, journey_mode: 'LUXURY' });
          }}
          style={{
            background: `linear-gradient(135deg, ${PJ.obsidian} 0%, #2c2220 100%)`,
            border: `1.5px solid ${PJ.gold}40`,
            borderRadius: 20, padding: '24px 28px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 20, textAlign: 'left',
            transition: 'all 0.2s', boxShadow: `0 4px 24px rgba(201,161,95,0.15)`,
          }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 8px 32px rgba(201,161,95,0.25)`; e.currentTarget.style.borderColor = `${PJ.gold}80`; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 4px 24px rgba(201,161,95,0.15)`; e.currentTarget.style.borderColor = `${PJ.gold}40`; }}
        >
          <div style={{ width: 52, height: 52, borderRadius: 14, background: `${PJ.gold}20`, border: `1px solid ${PJ.gold}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Gem size={24} color={PJ.gold} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: '1.15rem', color: PJ.ivory }}>Luxury Event</span>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase', background: PJ.gold, color: PJ.obsidian, borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>Concierge</span>
            </div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: `${PJ.ivory}90`, margin: 0, lineHeight: 1.45 }}>
              Premium end-to-end planning, with curated services automatically included.
            </p>
          </div>
          <ChevronRight size={20} color={PJ.gold} style={{ flexShrink: 0 }} />
        </button>

        {/* Custom Card */}
        <button
          data-testid="custom-mode-btn"
          onClick={() => {
            setJourneyMode('CUSTOM');
            const savedJourney = loadJourneyFromStorage() || {};
            saveJourneyToStorage({ ...savedJourney, journey_mode: 'CUSTOM' });
          }}
          style={{
            background: PJ.white, border: `1.5px solid ${PJ.sand}`, borderRadius: 20,
            padding: '24px 28px', cursor: 'pointer', display: 'flex', alignItems: 'center',
            gap: 20, textAlign: 'left', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = PJ.gold; e.currentTarget.style.boxShadow = `0 4px 16px rgba(32,27,25,0.08)`; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = PJ.sand; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div style={{ width: 52, height: 52, borderRadius: 14, background: PJ.blush, border: `1px solid ${PJ.sand}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Edit3 size={24} color={PJ.slate} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: '1.15rem', color: PJ.obsidian, margin: '0 0 4px' }}>Build My Own Event</p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.slate, margin: 0, lineHeight: 1.45 }}>
              Choose ceremonies, services, packages and details yourself.
            </p>
          </div>
          <ChevronRight size={20} color={PJ.slate} style={{ flexShrink: 0 }} />
        </button>
      </div>
    </div>
  );

  // ── GQP-LUX-01: Luxury Details Step ──────────────────────────────────────────
  const renderLuxuryDetails = () => {
    const WF = WEDDING_FUNCTIONS || [];
    const isWedding = ['WEDDING', 'ENGAGEMENT'].includes(luxuryState.eventType);
    return (
      <div className="pj-step-enter" style={{ maxWidth: 560 }} data-testid="lux-details-step">
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Gem size={16} color={PJ.gold} />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: PJ.gold }}>Luxury Concierge</span>
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.5rem,3.5vw,2rem)', fontWeight: 600, color: PJ.obsidian, margin: '0 0 6px' }}>
            Tell us about your event
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: PJ.slate, margin: 0 }}>
            A few essentials to build your curated luxury experience.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Event Type */}
          <div>
            <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 8 }}>Event Type *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
              {(EVENT_TYPES || []).slice(0, 6).map(et => {
                const Icon = EVENT_ICONS[et.value] || Star;
                const sel = luxuryState.eventType === et.value;
                return (
                  <button key={et.value} onClick={() => setLuxuryState(s => ({ ...s, eventType: et.value, functions: [] }))}
                    style={{ background: sel ? PJ.blush : PJ.white, border: `1.5px solid ${sel ? PJ.gold : PJ.sand}`, borderRadius: 12, padding: '10px 8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, transition: 'all 0.15s' }}>
                    <Icon size={16} color={sel ? PJ.gold : PJ.slate} />
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: sel ? PJ.obsidian : PJ.slate, fontWeight: sel ? 600 : 400 }}>{et.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* City */}
          <div>
            <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 8 }}>City / Location *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(CITIES || []).slice(0, 8).map(c => {
                const sel = luxuryState.city === c.value;
                return (
                  <button key={c.value} onClick={() => setLuxuryState(s => ({ ...s, city: c.value }))}
                    style={{ background: sel ? PJ.blush : PJ.white, border: `1.5px solid ${sel ? PJ.gold : PJ.sand}`, borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: sel ? PJ.obsidian : PJ.slate, transition: 'all 0.15s', fontWeight: sel ? 600 : 400 }}>
                    {c.label || c.value}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event Date */}
          <div>
            <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 8 }}>Event Date *</label>
            <input type="date" value={luxuryState.date} onChange={e => setLuxuryState(s => ({ ...s, date: e.target.value }))}
              min={new Date().toISOString().split('T')[0]}
              style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${PJ.sand}`, borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: PJ.obsidian, background: PJ.white, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Guest Count */}
          <div>
            <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 8 }}>Expected Guests *</label>
            <input type="number" value={luxuryState.guestCount} onChange={e => setLuxuryState(s => ({ ...s, guestCount: parseInt(e.target.value, 10) || '' }))}
              min={1} max={5000} placeholder="e.g. 300"
              style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${PJ.sand}`, borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: PJ.obsidian, background: PJ.white, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Wedding Functions */}
          {isWedding && (
            <div>
              <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 8 }}>
                Wedding Celebrations
                <span style={{ fontWeight: 400, color: PJ.slate, marginLeft: 4 }}>(select all that apply)</span>
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {WF.map(fn => {
                  const sel = luxuryState.functions.includes(fn.value);
                  return (
                    <button key={fn.value} onClick={() => setLuxuryState(s => ({
                      ...s, functions: sel ? s.functions.filter(f => f !== fn.value) : [...s.functions, fn.value]
                    }))}
                      style={{ background: sel ? PJ.blush : PJ.white, border: `1.5px solid ${sel ? PJ.gold : PJ.sand}`, borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: sel ? PJ.obsidian : PJ.slate, transition: 'all 0.15s', fontWeight: sel ? 600 : 400 }}>
                      {fn.label || fn.value}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Continue Button */}
          <button
            data-testid="lux-details-continue"
            disabled={!luxuryState.eventType || !luxuryState.city || !luxuryState.date || !luxuryState.guestCount}
            onClick={async () => {
              setLuxuryStep('LUX_ESTIMATE');
              await handleLoadLuxuryEstimate();
            }}
            style={{
              marginTop: 8, padding: '14px 28px', borderRadius: 12, cursor: 'pointer',
              background: (!luxuryState.eventType || !luxuryState.city || !luxuryState.date || !luxuryState.guestCount) ? PJ.sand : PJ.obsidian,
              color: (!luxuryState.eventType || !luxuryState.city || !luxuryState.date || !luxuryState.guestCount) ? PJ.slate : PJ.ivory,
              border: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s',
            }}
          >
            View My Curated Package <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  // ── GQP-LUX-01: Luxury Estimate Step ─────────────────────────────────────────
  const renderLuxuryEstimate = () => {
    const est = luxuryEstimate;
    const meta = est?.service_meta || [];
    const breakdowns = est?.service_breakdowns || [];

    return (
      <div className="pj-step-enter" style={{ maxWidth: 600 }} data-testid="lux-estimate-step">
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Gem size={16} color={PJ.gold} />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: PJ.gold }}>Your Curated Luxury Experience</span>
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.5rem,3.5vw,2rem)', fontWeight: 600, color: PJ.obsidian, margin: '0 0 4px' }}>
            {est?.template_display_name || 'Prashasta Luxury Package'}
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.slate, margin: 0 }}>
            {luxuryState.guestCount} guests · {luxuryState.city}
            {luxuryState.functions?.length > 0 && ` · ${luxuryState.functions.length} celebrations`}
          </p>
        </div>

        {luxuryLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: 12, color: PJ.slate }}>
            <div style={{ width: 20, height: 20, border: `2px solid ${PJ.sand}`, borderTopColor: PJ.gold, borderRadius: '50%', animation: 'pjSpin 0.8s linear infinite' }} />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem' }}>Building your luxury package…</span>
          </div>
        )}

        {luxuryError && (
          <div style={{ background: '#FFF5F5', border: '1px solid #FED7D7', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#C53030', margin: 0 }}>{luxuryError}</p>
            <button onClick={() => handleLoadLuxuryEstimate()} style={{ marginTop: 10, padding: '6px 14px', border: `1px solid ${PJ.sand}`, borderRadius: 8, background: PJ.white, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: PJ.slate }}>
              Try Again
            </button>
          </div>
        )}

        {!luxuryLoading && est && (
          <>
            {/* Included services */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {meta.map(svc => {
                const bd = breakdowns.find(b => b.service_id === svc.service_id);
                const isOptional = !svc.is_required;
                const isIncluded = svc.is_included;
                return (
                  <div key={svc.service_code}
                    style={{ background: isIncluded ? PJ.white : `${PJ.sand}40`, border: `1px solid ${isIncluded ? PJ.sand : PJ.sand}`, borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, opacity: isIncluded ? 1 : 0.55 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: isIncluded ? PJ.blush : PJ.sand, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check size={14} color={isIncluded ? PJ.gold : PJ.slate} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 500, color: PJ.obsidian, margin: '0 0 2px' }}>{svc.display_label}</p>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: PJ.slate, margin: 0 }}>{svc.description}</p>
                      {svc.allocate_per_function && svc.functions?.length > 0 && (
                        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', color: PJ.gold, margin: '2px 0 0', fontWeight: 500 }}>
                          Per celebration: {svc.functions.join(', ')}
                        </p>
                      )}
                    </div>
                    {bd && (
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: PJ.obsidian, flexShrink: 0 }}>{bd.line_total_display}</span>
                    )}
                    {isOptional && (
                      <button onClick={() => {
                        const isDisabled = luxuryState.disabledServices.includes(svc.service_code);
                        const next = isDisabled
                          ? { ...luxuryState, disabledServices: luxuryState.disabledServices.filter(c => c !== svc.service_code) }
                          : { ...luxuryState, disabledServices: [...luxuryState.disabledServices, svc.service_code] };
                        setLuxuryState(next);
                        handleLoadLuxuryEstimate(next);
                      }}
                        style={{ background: 'none', border: `1px solid ${PJ.sand}`, borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', color: PJ.slate, flexShrink: 0 }}>
                        {isIncluded ? 'Remove' : 'Add'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Estimate summary */}
            <div style={{ background: PJ.obsidian, borderRadius: 16, padding: '20px 24px', marginBottom: 20 }} data-testid="lux-estimate-summary">
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: `${PJ.ivory}60`, margin: '0 0 4px' }}>
                Your Indicative Estimate
              </p>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.4rem,3vw,1.8rem)', fontWeight: 600, color: PJ.ivory, margin: '0 0 4px' }}>
                {est.total_priced_display}
              </p>
              {est.range_minimum_display && (
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: `${PJ.ivory}70`, margin: '0 0 12px' }}>
                  Range: {est.range_minimum_display} – {est.range_maximum_display}
                </p>
              )}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[
                  { label: 'Guest count', value: luxuryState.guestCount },
                  { label: 'Celebrations', value: luxuryState.functions?.length || 1 },
                  { label: 'Service categories', value: meta.filter(s => s.is_included).length },
                ].map(stat => (
                  <div key={stat.label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', color: `${PJ.ivory}60`, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{stat.label}</span>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 600, color: PJ.ivory }}>{stat.value}</span>
                  </div>
                ))}
              </div>
              {est.has_incomplete_pricing && (
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: '#F6C90E', marginTop: 10, margin: '10px 0 0' }}>
                  Some items are subject to price on request. A Prashasta planner will confirm the complete estimate.
                </p>
              )}
            </div>

            {/* Disclaimer */}
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.73rem', color: PJ.slate, lineHeight: 1.5, marginBottom: 20 }}>
              This indicative estimate is calculated from the Prashasta published service catalog. Final pricing may vary based on venue availability, specific supplier selection, seasonality, and applicable taxes. Submitting this request does not constitute a booking or confirmed quotation.
            </p>

            <button data-testid="lux-estimate-continue"
              onClick={() => setLuxuryStep('LUX_REVIEW')}
              style={{ width: '100%', padding: '14px 28px', borderRadius: 12, cursor: 'pointer', background: PJ.obsidian, color: PJ.ivory, border: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              Review & Submit My Request <ChevronRight size={16} />
            </button>
          </>
        )}
      </div>
    );
  };

  // ── GQP-LUX-02: Callback preference component (shared) ────────────────────────
  const renderCallbackPreference = () => (
    <div style={{ background: PJ.blush, border: `1px solid ${PJ.sand}`, borderRadius: 14, padding: '18px 20px', marginTop: 8 }} data-testid="callback-preference-section">
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: callbackPref.requested ? 16 : 0 }}>
        <input type="checkbox" checked={callbackPref.requested} onChange={e => setCallbackPref(p => ({ ...p, requested: e.target.checked }))}
          style={{ width: 16, height: 16, accentColor: PJ.gold }} data-testid="callback-checkbox" />
        <div>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 500, color: PJ.obsidian }}>Request a preferred callback time</span>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: PJ.slate, margin: '2px 0 0' }}>
            Optional · Our team will aim to call at your preferred time
          </p>
        </div>
      </label>

      {callbackPref.requested && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Anytime toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={callbackPref.anytime} onChange={e => setCallbackPref(p => ({ ...p, anytime: e.target.checked, date: e.target.checked ? '' : p.date }))}
              style={{ width: 14, height: 14, accentColor: PJ.gold }} data-testid="callback-anytime-checkbox" />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: PJ.obsidian }}>Anytime during business hours (Mon–Sat, 9am–7pm IST)</span>
          </label>

          {!callbackPref.anytime && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 150 }}>
                <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 5 }}>Preferred Date</label>
                <input type="date" value={callbackPref.date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => setCallbackPref(p => ({ ...p, date: e.target.value }))}
                  data-testid="callback-date-input"
                  style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${PJ.sand}`, borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: '0.83rem', color: PJ.obsidian, background: PJ.white, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 130 }}>
                <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 5 }}>Time Window</label>
                <select value={callbackPref.window} onChange={e => setCallbackPref(p => ({ ...p, window: e.target.value }))}
                  data-testid="callback-window-select"
                  style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${PJ.sand}`, borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: '0.83rem', color: PJ.obsidian, background: PJ.white, outline: 'none', boxSizing: 'border-box' }}>
                  <option value="MORNING">Morning (9am–12pm)</option>
                  <option value="AFTERNOON">Afternoon (12pm–4pm)</option>
                  <option value="EVENING">Evening (4pm–7pm)</option>
                </select>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 130 }}>
              <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 5 }}>Preferred Method</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['PHONE', 'WHATSAPP'].map(m => (
                  <button key={m} onClick={() => setCallbackPref(p => ({ ...p, method: m }))}
                    data-testid={`callback-method-${m.toLowerCase()}`}
                    style={{ flex: 1, padding: '7px 10px', border: `1.5px solid ${callbackPref.method === m ? PJ.gold : PJ.sand}`, borderRadius: 8, background: callbackPref.method === m ? PJ.blush : PJ.white, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: PJ.obsidian, fontWeight: callbackPref.method === m ? 600 : 400 }}>
                    {m === 'PHONE' ? 'Phone' : 'WhatsApp'}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1.5, minWidth: 150 }}>
              <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 5 }}>Time Zone</label>
              <select value={callbackPref.timezone} onChange={e => setCallbackPref(p => ({ ...p, timezone: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${PJ.sand}`, borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: '0.83rem', color: PJ.obsidian, background: PJ.white, outline: 'none', boxSizing: 'border-box' }}>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Asia/Dubai">Dubai (GST)</option>
                <option value="America/New_York">New York (ET)</option>
                <option value="America/Los_Angeles">Los Angeles (PT)</option>
                <option value="Europe/London">London (GMT/BST)</option>
                <option value="Asia/Singapore">Singapore (SGT)</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 5 }}>Additional notes (optional)</label>
            <textarea value={callbackPref.notes} onChange={e => setCallbackPref(p => ({ ...p, notes: e.target.value }))}
              rows={2} placeholder="e.g. please call on WhatsApp, I am travelling…"
              data-testid="callback-notes-input"
              style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${PJ.sand}`, borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: '0.83rem', color: PJ.obsidian, background: PJ.white, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: PJ.slate, margin: 0, lineHeight: 1.5 }}>
            Providing a callback preference does not guarantee an appointment at that exact time. Your Prashasta relationship manager will aim to call within the window you select.
          </p>
        </div>
      )}
    </div>
  );

  // ── GQP-LUX-01: Luxury Review & Submit Step ───────────────────────────────────
  const renderLuxuryReview = () => {
    const est = luxuryEstimate;
    const isSubmitting = submitState.status === 'submitting';
    const isError = submitState.status === 'error';
    return (
      <div className="pj-step-enter" style={{ maxWidth: 560 }} data-testid="lux-review-step">
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Gem size={16} color={PJ.gold} />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: PJ.gold }}>Review & Submit</span>
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.5rem,3.5vw,2rem)', fontWeight: 600, color: PJ.obsidian, margin: '0 0 6px' }}>
            Your Luxury Event Request
          </h2>
        </div>

        {/* Summary card */}
        {est && (
          <div style={{ background: PJ.obsidian, borderRadius: 14, padding: '18px 22px', marginBottom: 20 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: `${PJ.ivory}60`, margin: '0 0 6px' }}>
              Prashasta Luxury Concierge
            </p>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.4rem', fontWeight: 600, color: PJ.ivory, margin: '0 0 4px' }}>
              {est.total_priced_display}
            </p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: `${PJ.ivory}70`, margin: '0 0 12px' }}>
              {luxuryState.guestCount} guests · {luxuryState.city}
              {luxuryState.functions?.length > 0 && ` · ${luxuryState.functions.length} celebrations`}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(est.service_meta || []).filter(s => s.is_included).map(s => (
                <span key={s.service_code} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', background: `${PJ.ivory}15`, color: `${PJ.ivory}90`, borderRadius: 20, padding: '3px 10px', border: `1px solid ${PJ.ivory}20` }}>
                  {s.display_label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Contact details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 600, color: PJ.obsidian, margin: 0 }}>Your Contact Details</h3>
          {[
            { key: 'name', label: 'Full Name *', placeholder: 'Your name', type: 'text' },
            { key: 'mobile', label: 'Mobile Number *', placeholder: '+91 98765 43210', type: 'tel' },
            { key: 'email', label: 'Email Address', placeholder: 'your@email.com', type: 'email' },
          ].map(field => (
            <div key={field.key}>
              <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 500, color: PJ.obsidian, display: 'block', marginBottom: 5 }}>{field.label}</label>
              <input type={field.type} value={contactForm[field.key]} onChange={e => setContactForm(f => ({ ...f, [field.key]: e.target.value }))}
                placeholder={field.placeholder}
                style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${PJ.sand}`, borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: PJ.obsidian, background: PJ.white, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          ))}
        </div>

        {/* Callback preference */}
        {renderCallbackPreference()}

        {/* Consent */}
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <label style={{ display: 'flex', gap: 10, cursor: 'pointer', alignItems: 'flex-start' }}>
            <input type="checkbox" checked={consentGiven} onChange={e => setConsentGiven(e.target.checked)}
              style={{ marginTop: 3, width: 15, height: 15, accentColor: PJ.gold, flexShrink: 0 }} data-testid="lux-consent-checkbox" />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: PJ.slate, lineHeight: 1.55 }}>
              I agree that Prashasta Events may contact me regarding my event request by phone, WhatsApp or email, using the contact details I provided.
            </span>
          </label>
        </div>

        {isError && (
          <div style={{ background: '#FFF5F5', border: '1px solid #FED7D7', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#C53030', margin: 0 }}>
              Submission failed. Please try again — your details are safe.
            </p>
          </div>
        )}

        <button
          data-testid="lux-submit-btn"
          disabled={!consentGiven || !contactForm.name?.trim() || !contactForm.mobile?.trim() || isSubmitting}
          onClick={async () => {
            // Sync luxury contact details to journeyState before submitting
            const updatedJourney = {
              ...journeyState,
              contact: { name: contactForm.name, mobile: contactForm.mobile, email: contactForm.email },
              event_type: { value: luxuryState.eventType },
              city: { value: luxuryState.city },
              event_date: { value: luxuryState.date },
              wedding_functions: { value: luxuryState.functions },
              journey_mode: 'LUXURY',
            };
            setJourneyState(updatedJourney);
            saveJourneyToStorage(updatedJourney);
            await handleSubmitRequest();
            setCurrentStepId('RESULT');
          }}
          style={{
            width: '100%', padding: '16px 28px', borderRadius: 12, cursor: (!consentGiven || !contactForm.name?.trim() || !contactForm.mobile?.trim() || isSubmitting) ? 'not-allowed' : 'pointer',
            background: (!consentGiven || !contactForm.name?.trim() || !contactForm.mobile?.trim()) ? PJ.sand : PJ.obsidian,
            color: (!consentGiven || !contactForm.name?.trim() || !contactForm.mobile?.trim()) ? PJ.slate : PJ.ivory,
            border: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'all 0.2s',
          }}
        >
          {isSubmitting ? (
            <>
              <div style={{ width: 18, height: 18, border: `2px solid ${PJ.sand}`, borderTopColor: PJ.ivory, borderRadius: '50%', animation: 'pjSpin 0.8s linear infinite' }} />
              Submitting…
            </>
          ) : (
            <>
              <Gem size={16} />
              Request My Luxury Event Proposal
            </>
          )}
        </button>
      </div>
    );
  };

  // ── Step router ──────────────────────────────────────────────────────────────
  const renderCurrentStep = () => {
    if (quickMode) return <QuickCallMode onDone={() => setQuickMode(false)} onSwitch={() => setQuickMode(false)} />;
    // GQP-LUX-01: Mode selection (for new sessions only)
    if (journeyMode === null) return renderModeSelect();
    // GQP-LUX-01: Luxury journey
    if (journeyMode === 'LUXURY' && currentStepId !== 'RESULT') {
      switch (luxuryStep) {
        case 'LUX_DETAILS':  return renderLuxuryDetails();
        case 'LUX_ESTIMATE': return renderLuxuryEstimate();
        case 'LUX_REVIEW':   return renderLuxuryReview();
        default:             return renderLuxuryDetails();
      }
    }
    // Custom journey (existing step sequence)
    switch (currentStepId) {
      case 'EVENT_TYPE':        return renderEventType();
      case 'WEDDING_FUNCTIONS': return renderWeddingFunctions();
      case 'CONFERENCE_TYPE':   return renderConferenceType();
      case 'PLANNING_SCOPE':    return renderPlanningScope();
      case 'SCOPE_CONFIRM':     return renderScopeConfirm();
      case 'SERVICE_SELECTION': return renderServiceSelection();
      case 'CITY':              return renderCity();
      case 'STYLE_PREF':        return renderStylePref();
      case 'GUEST_COUNT':       return renderGuestCount();
      case 'VENUE_STATUS':      return renderVenueStatus();
      case 'DATE':              return renderDate();
      case 'ESTIMATE':          return renderEstimate();
      case 'BUDGET':            return renderEstimate(); // backward compat: old drafts with BUDGET step
      case 'CONTACT':           return renderContact();
      case 'RESULT':            return renderResult();
      default:                  return renderEventType();
    }
  };

  const isResult = currentStepId === 'RESULT';
  const showProgress = !quickMode && !isResult && journeyMode === 'CUSTOM';
  const isLuxury = journeyMode === 'LUXURY';
  const showBack = !isResult && journeyMode !== null && (
    isLuxury
      ? luxuryStep !== 'LUX_DETAILS'
      : currentStepId !== 'EVENT_TYPE'
  );

  const handleBack = () => {
    if (isLuxury) {
      if (luxuryStep === 'LUX_ESTIMATE') { setLuxuryStep('LUX_DETAILS'); return; }
      if (luxuryStep === 'LUX_REVIEW') { setLuxuryStep('LUX_ESTIMATE'); return; }
    } else {
      // existing back logic handled by progressBar component
    }
  };

  return (
    <div
      data-testid="build-my-event-page"
      style={{ fontFamily: "'DM Sans', sans-serif", background: PJ.ivory, minHeight: '100vh' }}
    >
      <style>{JOURNEY_STYLES}</style>

      {/* ── Navigation bar ── */}
      <nav
        style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: `${PJ.ivory}f2`, backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${PJ.sand}80`,
          padding: '0 max(24px, 5vw)',
          height: '60px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
        }}
      >
        <button
          onClick={() => navigate('/event-journey')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: PJ.slate, display: 'flex', alignItems: 'center', gap: '6px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', padding: '6px 0', transition: 'color 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.color = PJ.obsidian; }}
          onMouseLeave={e => { e.currentTarget.style.color = PJ.slate; }}
        >
          <ChevronLeft size={16} />
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: '1.1rem', letterSpacing: '0.03em', color: 'inherit' }}>Prashasta</span>
        </button>

        {showProgress && (
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <JourneyProgress progress={progress} />
          </div>
        )}

        <button
          onClick={() => setWhatsappOpen(true)}
          style={{
            background: 'none', border: `1px solid ${PJ.sand}`, borderRadius: '100px',
            padding: '7px 14px', cursor: 'pointer', color: PJ.slate,
            fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem',
            display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.15s', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = PJ.obsidian; e.currentTarget.style.color = PJ.obsidian; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = PJ.sand; e.currentTarget.style.color = PJ.slate; }}
        >
          <MessageCircle size={13} /> WhatsApp
        </button>
      </nav>

      {/* ── Main Layout: Question + Sidebar ── */}
      <div
        style={{
          maxWidth: '1200px', margin: '0 auto',
          padding: '0 max(24px, 5vw)',
          display: 'grid',
          gridTemplateColumns: isResult ? '1fr' : '1fr 320px',
          gap: '60px',
          minHeight: 'calc(100vh - 60px)',
          alignItems: 'start',
        }}
        className="pj-main-grid"
      >
        {/* ── Question Area ── */}
        <div style={{ paddingTop: '60px', paddingBottom: '120px' }}>
          {/* Date already confirmed banner */}
          {journeyState?.event_date?.confirmed && currentStepId !== 'DATE' && currentStepId !== 'RESULT' && currentStepId !== 'EVENT_TYPE' && (
            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '100px', background: `${PJ.forest}10`, border: `1px solid ${PJ.forest}30`, width: 'fit-content' }}>
              <Check size={13} style={{ color: PJ.forest }} />
              <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: PJ.forest }}>
                Date confirmed: {journeyState.event_date.display}
              </p>
              <button
                onClick={() => {
                  const next = { ...journeyState };
                  delete next.event_date;
                  setJourneyState(next);
                  saveJourneyToStorage(next);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: PJ.slate, padding: '0 0 0 4px', fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif" }}
              >
                Change
              </button>
            </div>
          )}

          {/* Inspired-by banner (EXP-01E) — visible until inspiration is removed or RESULT reached */}
          {journeyState?.inspiration?.selected && currentStepId !== 'RESULT' && (
            <div
              data-testid="bme-inspiration-banner"
              style={{
                marginBottom: '20px',
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 16px', borderRadius: '100px',
                background: `${PJ.gold}18`, border: `1px solid ${PJ.gold}45`,
                width: 'fit-content', flexWrap: 'wrap',
              }}
            >
              <Star size={13} style={{ color: PJ.gold, flexShrink: 0 }} />
              <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: PJ.obsidian }}>
                Inspired by{' '}
                <strong style={{ color: PJ.ruby }}>{journeyState.inspiration.inspiration_title}</strong>
                {' '}— suggestions applied. Edit freely.
              </p>
              <button
                data-testid="remove-inspiration-btn"
                onClick={handleRemoveInspiration}
                title="Remove inspiration — clears only unconfirmed suggestions"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: PJ.slate, padding: '0 0 0 4px',
                  fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif",
                  display: 'flex', alignItems: 'center', gap: '3px',
                  whiteSpace: 'nowrap',
                }}
              >
                <X size={12} /> Remove
              </button>
            </div>
          )}

          {renderCurrentStep()}

          {/* Back button */}
          {showBack && (
            <div style={{ marginTop: '32px' }}>
              <button
                data-testid="journey-back-btn"
                onClick={isLuxury ? handleBack : goBack}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem',
                  color: PJ.slate, display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 0', transition: 'color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = PJ.obsidian; }}
                onMouseLeave={e => { e.currentTarget.style.color = PJ.slate; }}
              >
                <ChevronLeft size={15} /> Back
              </button>
            </div>
          )}
        </div>

        {/* ── Sidebar (desktop only, hidden on mobile via CSS) ── */}
        {!isResult && (
          <div
            className="pj-sidebar"
            style={{
              position: 'sticky', top: '80px',
              borderRadius: '24px',
              marginTop: '40px',
            }}
          >
            {/* ── Sidebar toggle tabs ── */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
              <button
                data-testid="sidebar-tab-plan"
                className="pj-sidebar-tab"
                onClick={() => setSidebarMode('PLAN')}
                style={{
                  flex: 1, padding: '7px 10px', borderRadius: '100px',
                  background: sidebarMode === 'PLAN' ? PJ.obsidian : 'transparent',
                  color: sidebarMode === 'PLAN' ? PJ.white : PJ.slate,
                  border: `1px solid ${sidebarMode === 'PLAN' ? PJ.obsidian : PJ.sand}`,
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 600,
                  cursor: 'pointer', letterSpacing: '0.01em',
                }}
              >
                Your Plan
              </button>
              <button
                data-testid="sidebar-tab-ask"
                className="pj-sidebar-tab"
                onClick={() => setSidebarMode('ASK')}
                style={{
                  flex: 1, padding: '7px 10px', borderRadius: '100px',
                  background: sidebarMode === 'ASK' ? `${PJ.ruby}12` : 'transparent',
                  color: sidebarMode === 'ASK' ? PJ.ruby : PJ.slate,
                  border: `1px solid ${sidebarMode === 'ASK' ? `${PJ.ruby}50` : PJ.sand}`,
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 600,
                  cursor: 'pointer', letterSpacing: '0.01em',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                }}
              >
                {sidebarMode !== 'ASK' && <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: PJ.ruby, display: 'inline-block' }} />}
                Ask Prashasta
              </button>
            </div>

            {/* ── Sidebar content ── */}
            <div
              style={{
                background: PJ.white, borderRadius: '20px',
                padding: '24px',
                border: `1px solid ${PJ.sand}`,
                maxHeight: 'calc(100vh - 160px)',
                overflowY: sidebarMode === 'ASK' ? 'auto' : 'visible',
              }}
            >
              {sidebarMode === 'PLAN' ? (
                <LiveEventPlan state={journeyState} />
              ) : (
                <AskPrashasta
                  currentStepId={currentStepId}
                  onAskPlanner={() => setWhatsappOpen(true)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Mobile: "Need help?" floating pill ── */}
      {!isResult && (
        <div
          className="pj-ask-float"
          style={{
            display: 'none', // shown via media query
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)',
            right: '16px',
            zIndex: 180,
          }}
        >
          <button
            data-testid="ask-float-btn"
            onClick={() => setAskSheetOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: PJ.white, color: PJ.ruby,
              border: `1.5px solid ${PJ.ruby}40`,
              borderRadius: '100px',
              padding: '8px 16px',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.78rem',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(217,30,72,0.14)',
            }}
          >
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: PJ.ruby, display: 'inline-block' }} />
            Need help?
          </button>
        </div>
      )}

      {/* ── Mobile Ask Prashasta bottom sheet ── */}
      <AskPrashastaSheet
        isOpen={askSheetOpen}
        onClose={() => setAskSheetOpen(false)}
        currentStepId={currentStepId}
        onAskPlanner={() => { setAskSheetOpen(false); setWhatsappOpen(true); }}
      />

      {/* ── Mobile bottom bar ── */}
      {!isResult && (
        <div
          style={{
            display: 'none', // shown via media query override
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
            background: PJ.white, borderTop: `1px solid ${PJ.sand}`,
            padding: '12px 20px',
            alignItems: 'center', justifyContent: 'space-between', gap: '12px',
          }}
          className="pj-mobile-plan"
        >
          <button
            onClick={() => setShowPlanMobile(v => !v)}
            style={{
              background: PJ.ivory, border: `1px solid ${PJ.sand}`,
              borderRadius: '100px', padding: '8px 16px', cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: PJ.slate,
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            My Event {showPlanMobile ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
          <button
            onClick={() => setWhatsappOpen(true)}
            style={{
              background: '#25D366', color: PJ.white, border: 'none', borderRadius: '100px',
              padding: '10px 20px', cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.82rem',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <MessageCircle size={14} /> WhatsApp
          </button>
        </div>
      )}

      {/* Mobile event plan drawer */}
      {showPlanMobile && !isResult && (
        <div
          style={{
            position: 'fixed', bottom: '60px', left: 0, right: 0, zIndex: 39,
            background: PJ.white, borderTop: `1px solid ${PJ.sand}`,
            padding: '24px 20px', maxHeight: '60vh', overflowY: 'auto',
            animation: 'pjFadeSlideIn 0.25s ease both',
          }}
        >
          <LiveEventPlan state={journeyState} />
        </div>
      )}

      {/* WhatsApp modal */}
      {whatsappOpen && (
        <WhatsAppModal state={journeyState} onClose={() => setWhatsappOpen(false)} />
      )}
    </div>
  );
}
